// src/pages/ManageAssets.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableSortLabel,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Stack,
  FormControl,
  FormHelperText,
  InputLabel,
  Chip,
  Divider,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  LoadingButton,
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
} from "@mui/lab";
import Autocomplete from "@mui/material/Autocomplete";
import Add from "@mui/icons-material/Add";
import { AssetsAPI } from "../api/assets";
import { DevicesAPI } from "../api/devices";
import { ROUTES } from "../routes";
import { useNavigate } from "react-router-dom";
import StatCard from "../components/StatCard";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import MemoryIcon from "@mui/icons-material/Memory";
import AppsIcon from "@mui/icons-material/Apps";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import StorageIcon from "@mui/icons-material/Storage";
import RouterIcon from "@mui/icons-material/Router";
import PrintIcon from "@mui/icons-material/Print";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { UploadFile } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { EnterprisesAPI } from "../api/enterprises";
import { TrendingUp, WarningAmber, CheckCircle } from "@mui/icons-material";
import HistoryIcon from "@mui/icons-material/History";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { ReportsAPI } from "../api/reports";
import { AssetCustomFieldsAPI } from "../api/assetCustomFields";
import AssetCustomFieldInputs from "../components/AssetCustomFieldInputs";
import AssetCustomFieldsDialog from "../components/AssetCustomFieldsDialog";
import { useLocation } from "react-router-dom";

/**
 * Upgraded ManageAssets page
 * - Full create/edit/view/assign dialogs taken from EnterpriseDashboard assets tab
 * - Search, sort, export CSV, device assign
 * - Listens to openAssetCreate custom event (dispatched by Layout side-nav Add New Asset)
 */

const emptyAsset = {
  device_id: "",
  purchase_date: "",
  vendor_name: "",
  purchase_price: "",
  purchase_currency: "INR",
  license_start: "",
  license_end: "",
  warranty_start: "",
  warranty_end: "",
  location: "",
  remarks: "",
  invoice_url: "",
  amc_status: "None",
  cmc_status: "None",
  amc_renewal_date: "",
  cmc_renewal_date: "",
  allocation_date: "",
  owner_name: "",
  owner_email: "",
  owner_contact: "",
  asset_custodian: "",
  asset_custodian_email: "",
  asset_custodian_contact: "",
  department: "",
  asset_type: "",
  asset_status: "IN_STOCK",
  delivery_status: "ORDERED",
  quantity: 1,
  depreciation_method: "SLM",
  useful_life_months: "",
  depreciation_rate_percent: "",
  salvage_value: 0,
  depreciation_start_mode: "PURCHASE_DATE",
  details: {},
};

const COMMON_ASSET_TYPE_SUGGESTIONS = [
  "DEVICE",
  "RAM",
  "SSD",
  "HDD",
  "GPU",
  "LICENSE",
  "PERIPHERAL",
  "SOFTWARE",
];

const BACKEND_ASSET_STATUS = [
  "IN_STOCK",
  "ASSIGNED",
  "IN_REPAIR",
  "RETIRED",
  "DISPOSED",
  "ALLOCATED",
  "NOT_ALLOCATED",
  "SCRAP",
];

const BACKEND_DELIVERY_STATUS = ["ORDERED", "DISPATCHED", "RECEIVED"];
const ASSET_SOURCE_LABELS = {
  AGENT: "Agent discovered",
  MANUAL: "Manual entry",
  CSV_IMPORT: "Spreadsheet import",
  TALLY_IMPORT: "Accounting import",
  LEGACY_UNKNOWN: "Source not recorded",
};
const assetSourceLabel = (source) => ASSET_SOURCE_LABELS[source] || "Source not recorded";
const MAX_ASSET_IMAGE_COUNT = 3;
const MAX_ASSET_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
const PURCHASE_CURRENCY_OPTIONS = ["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD", "JPY"];
const DEPRECIATION_METHOD_OPTIONS = [
  { value: "SLM", label: "Straight-Line (SLM)" },
  { value: "WDV", label: "Written Down Value (WDV)" },
];
const DEPRECIATION_START_MODE_OPTIONS = [
  { value: "PURCHASE_DATE", label: "Start from purchase month" },
  { value: "NEXT_MONTH", label: "Start from next month" },
];

function getAssetDetails(asset) {
  return asset?.details && typeof asset.details === "object" ? asset.details : {};
}

function normalizeAssetTypeValue(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toUpperCase();
}

function humanizeAssetTypeLabel(value) {
  const key = normalizeAssetTypeValue(value);
  const known = {
    DEVICE: "Laptop",
    RAM: "RAM",
    SSD: "SSD",
    HDD: "HDD",
    GPU: "GPU",
    LICENSE: "License",
    PERIPHERAL: "Peripheral",
    SOFTWARE: "Software",
    ROUTER: "Router",
    SWITCH: "Switch",
    FIREWALL: "Firewall",
    NETWORK: "Network",
    MOBILE: "Mobile",
    PHONE: "Phone",
    TABLET: "Tablet",
    PRINTER: "Printer",
    CPU: "CPU",
  };
  if (known[key]) return known[key];
  return key
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function getDefaultUsefulLifeMonths(assetType) {
  const type = String(assetType || "").toUpperCase();
  if (["DEVICE"].includes(type)) return 60;
  if (["RAM", "SSD", "HDD", "GPU"].includes(type)) return 48;
  if (["PERIPHERAL"].includes(type)) return 36;
  return 48;
}

function getDepreciationFormValues(asset) {
  const details = getAssetDetails(asset);
  const assetType = String(asset?.asset_type || "").toUpperCase();
  const usefulLifeMonths = details.useful_life_months ?? (details.useful_life_years != null ? Number(details.useful_life_years) * 12 : null);
  return {
    depreciation_method: String(details.depreciation_method || "SLM").toUpperCase(),
    useful_life_months: usefulLifeMonths ?? getDefaultUsefulLifeMonths(assetType),
    depreciation_rate_percent: details.depreciation_rate_percent ?? "",
    salvage_value: details.salvage_value ?? details.residual_value ?? 0,
    depreciation_start_mode: String(details.depreciation_start_mode || "PURCHASE_DATE").toUpperCase(),
  };
}

function normalizeAssetPayload(payload) {
  const normalized = { ...payload };
  if (normalized.asset_type) normalized.asset_type = String(normalized.asset_type).toUpperCase();
  if (normalized.asset_status) normalized.asset_status = String(normalized.asset_status).toUpperCase();
  if (normalized.delivery_status) normalized.delivery_status = String(normalized.delivery_status).toUpperCase();
  if (normalized.depreciation_method) normalized.depreciation_method = String(normalized.depreciation_method).toUpperCase();
  if (normalized.depreciation_start_mode) normalized.depreciation_start_mode = String(normalized.depreciation_start_mode).toUpperCase();
  if (normalized.quantity != null && normalized.quantity !== "") {
    const qty = Number(normalized.quantity);
    normalized.quantity = Number.isFinite(qty) ? qty : normalized.quantity;
  }
  return normalized;
}

function buildDepreciationDetails(form) {
  const details = getAssetDetails(form);
  const usefulLifeMonths = form.useful_life_months === "" || form.useful_life_months == null ? "" : Number(form.useful_life_months);
  const depreciationRatePercent = form.depreciation_rate_percent === "" || form.depreciation_rate_percent == null
    ? ""
    : Number(form.depreciation_rate_percent);
  const salvageValue = form.salvage_value === "" || form.salvage_value == null ? 0 : Number(form.salvage_value);

  const nextDetails = {
    ...details,
    purchase_currency: String(form.purchase_currency || details.purchase_currency || "INR").toUpperCase(),
    depreciation_method: String(form.depreciation_method || "SLM").toUpperCase(),
    depreciation_start_mode: String(form.depreciation_start_mode || "PURCHASE_DATE").toUpperCase(),
    salvage_value: Number.isFinite(salvageValue) ? salvageValue : 0,
  };

  if (usefulLifeMonths !== "") nextDetails.useful_life_months = Number.isFinite(usefulLifeMonths) ? usefulLifeMonths : details.useful_life_months;
  else delete nextDetails.useful_life_months;
  delete nextDetails.useful_life_years;

  if (depreciationRatePercent !== "") nextDetails.depreciation_rate_percent = Number.isFinite(depreciationRatePercent) ? depreciationRatePercent : details.depreciation_rate_percent;
  else delete nextDetails.depreciation_rate_percent;

  return nextDetails;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function getAssetErrorMessage(error) {
  const apiError = error?.response?.data?.error;
  if (!apiError) return error?.message || "Failed to save asset";
  if (typeof apiError === "string" && /asset_uid must be unique/i.test(apiError)) {
    return "Asset ID already exists. Use a unique Asset ID.";
  }
  if (typeof apiError === "string" && /not found for this enterprise/i.test(apiError)) {
    return "Selected device is not available in your enterprise.";
  }
  if (typeof apiError === "string" && /validation error/i.test(apiError)) {
    return "Asset validation failed. Check required fields and allowed dropdown values.";
  }
  if (typeof apiError === "string" && /azure blob upload failed/i.test(apiError)) {
    return apiError;
  }
  return apiError || error?.message || "Failed to save asset";
}
function getAssetTypeMeta(type) {
  const key = String(type || "").toUpperCase();
  const label = humanizeAssetTypeLabel(key) || key;
  if (key === "DEVICE") return { label, color: "#0b5cad", icon: <LaptopMacIcon fontSize="small" /> };
  if (["RAM", "GPU", "CPU"].includes(key)) return { label, color: "#0e9f6e", icon: <MemoryIcon fontSize="small" /> };
  if (["SSD", "HDD"].includes(key)) return { label, color: "#b45309", icon: <StorageIcon fontSize="small" /> };
  if (["ROUTER", "SWITCH", "FIREWALL", "NETWORK"].includes(key)) return { label, color: "#7c3aed", icon: <RouterIcon fontSize="small" /> };
  if (["PERIPHERAL", "PRINTER"].includes(key)) return { label, color: "#475569", icon: <PrintIcon fontSize="small" /> };
  if (["MOBILE", "PHONE", "TABLET"].includes(key)) return { label, color: "#0891b2", icon: <PhoneAndroidIcon fontSize="small" /> };
  if (["SOFTWARE", "LICENSE"].includes(key)) return { label, color: "#1d4ed8", icon: <AppsIcon fontSize="small" /> };
  return { label, color: "#334155", icon: <DevicesOtherIcon fontSize="small" /> };
}

function getDeliveryStatusMeta(value) {
  if (!value) return { label: "Not recorded", bg: "#F1F5F9", fg: "#475569" };
  const v = String(value).toUpperCase();
  if (v === "RECEIVED") return { label: "Received", bg: "#DCFCE7", fg: "#166534" };
  if (v === "DISPATCHED") return { label: "Dispatched", bg: "#FEF3C7", fg: "#92400E" };
  return { label: "Ordered", bg: "#E0F2FE", fg: "#075985" };
}

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function buildCloneAssetForm(source) {
  const a = source || {};
  const depreciation = getDepreciationFormValues(a);
  return {
    ...emptyAsset,
    ...a,
    // Clone should create a new record identity, not copy server-managed IDs.
    device_id: "",
    purchase_date: fmtDate(a.purchase_date),
    warranty_start: fmtDate(a.warranty_start),
    warranty_end: fmtDate(a.warranty_end),
    license_start: fmtDate(a.license_start),
    license_end: fmtDate(a.license_end),
    amc_renewal_date: fmtDate(a.amc_renewal_date),
    cmc_renewal_date: fmtDate(a.cmc_renewal_date),
    allocation_date: fmtDate(a.allocation_date),
    purchase_currency: a.purchase_currency || a.details?.purchase_currency || "INR",
    details: a.details && typeof a.details === "object" ? { ...a.details } : {},
    ...depreciation,
  };
}

function getAssetLifecycleLabel(asset) {
  const status = String(asset?.asset_status || "").toUpperCase();
  if (status === "RETIRED") return "Retired";
  if (status === "DISPOSED" || status === "SCRAP") return "Disposed";
  if (status === "IN_REPAIR") return "In Repair";
  // Ownership is mandatory and belongs to the organisation. It must not make an
  // asset look assigned: assignment happens only when it has a custodian or a device.
  return asset?.device_id || asset?.asset_custodian ? "Assigned" : "Not assigned";
}

function getAssetCurrency(asset) {
  return String(asset?.purchase_currency || asset?.details?.purchase_currency || "INR").toUpperCase();
}

function formatPurchaseAmount(asset) {
  if (asset?.purchase_price == null || asset?.purchase_price === "") return "—";
  const currency = getAssetCurrency(asset);
  const amount = Number(asset.purchase_price);
  if (!Number.isFinite(amount)) return "—";
  return `${currency} ${amount.toLocaleString()}`;
}

function toDMY(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${dt.getFullYear()}`;
}
const csvSafe = v => (v == null ? "" : String(v).replace(/"/g, '""'));
const toCSV = (rows, columns) => {
  const header = columns.map(c => `"${csvSafe(c.header)}"`).join(",");
  const body = rows.map(r =>
    columns
      .map(c => {
        let val = typeof c.get === "function" ? c.get(r) : r[c.key];
        if (val && typeof val === "object") val = JSON.stringify(val);
        return `"${csvSafe(val)}"`;
      })
      .join(",")
  );
  return [header, ...body].join("\n");
};
const downloadCSV = (csv, filename) => {
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const getPublicAssetLink = (asset) => {
  if (!asset) return "";
  if (asset.qr_payload_url) return asset.qr_payload_url;
  const id = encodeURIComponent(asset.asset_uid || asset.id);
  return `${window.location.origin}/asset-details/${id}`;
};

const getSafeQrImageUrl = (asset) => {
  if (!asset) return "";
  const raw = String(asset.qr_code_url || "");
  const hasLocalhost = /localhost|127\.0\.0\.1/i.test(raw);
  if (raw && !hasLocalhost) return raw;

  const text = getPublicAssetLink(asset);
  const logo = "https://itam.sushaservices.com/company-logo.png";
  const params = new URLSearchParams({
    text,
    size: "420",
    ecLevel: "H",
    margin: "1",
    format: "png",
    centerImageUrl: logo,
  });
  return `https://quickchart.io/qr?${params.toString()}`;
};

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

const downloadBrandedQrImage = async (asset) => {
  const qrUrl = getSafeQrImageUrl(asset);
  if (!qrUrl) return;

  const assetId = String(asset?.asset_uid || asset?.id || "N/A");
  const footerText = "Track your asset with Susha www.sushaservices.com";

  try {
    const qrImg = await loadImage(qrUrl);
    const canvas = document.createElement("canvas");
    const width = 560;
    const qrSize = 420;
    const topPad = 24;
    const topTextHeight = 44;
    const gap = 16;
    const footerHeight = 52;
    const bottomPad = 20;
    const height = topPad + topTextHeight + qrSize + gap + footerHeight + bottomPad;

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      window.open(qrUrl, "_blank", "noopener,noreferrer");
      return;
    }

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillStyle = "#0F172A";
    ctx.font = "bold 28px Arial";
    ctx.fillText(assetId, width / 2, topPad + topTextHeight / 2);

    const qrX = (width - qrSize) / 2;
    const qrY = topPad + topTextHeight;
    ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

    ctx.fillStyle = "#334155";
    ctx.font = "20px Arial";
    ctx.fillText(footerText, width / 2, qrY + qrSize + gap + footerHeight / 2);

    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `asset-qr-${assetId}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    window.open(qrUrl, "_blank", "noopener,noreferrer");
  }
};

const fmtDateTime = (value) => {
  if (!value) return "Never";
  const d = new Date(value);
  return isNaN(d.getTime()) ? "Never" : d.toLocaleString();
};

const fadeInUp = {
  "@keyframes pageFadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

const sectionMotion = delay => ({
  ...fadeInUp,
  opacity: 0,
  animation: `pageFadeUp 360ms ease ${delay}ms forwards`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    opacity: 1,
    transform: "none",
  },
});

export default function ManageAssets() {
  const navigate = useNavigate();
  const location = useLocation();
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [enterprisePlan, setEnterprisePlan] = useState(null);
  const [assetCustomFields, setAssetCustomFields] = useState([]);
  const [assetCustomFieldsOpen, setAssetCustomFieldsOpen] = useState(false);

  // bulk upload
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState("");
  const fileInputRef = useRef(null);

  // filters / UI state
  const [assetQ, setAssetQ] = useState("");
  const [assetSort, setAssetSort] = useState({ by: "purchase_date", dir: "desc" });
  const [assetTypeFilter, setAssetTypeFilter] = useState("ALL");
  const [deliveryFilter, setDeliveryFilter] = useState("ALL");
  const [lifecycleFilter, setLifecycleFilter] = useState("ACTIVE");
  const [sourceFilter, setSourceFilter] = useState("ALL");

  // dialogs
  const [assetCreateOpen, setAssetCreateOpen] = useState(false);
  const [assetEditOpen, setAssetEditOpen] = useState(false);
  const [assetViewOpen, setAssetViewOpen] = useState(false);
  const [assetAssignOpen, setAssetAssignOpen] = useState(false);
  const [custodyTransferOpen, setCustodyTransferOpen] = useState(false);
  const [assetHistoryOpen, setAssetHistoryOpen] = useState(false);
  const [assetHistoryLoading, setAssetHistoryLoading] = useState(false);
  const [assetHistoryRows, setAssetHistoryRows] = useState([]);
  const [assetHistoryQuery, setAssetHistoryQuery] = useState("");
  const [assetHistoryFrom, setAssetHistoryFrom] = useState("");
  const [assetHistoryTo, setAssetHistoryTo] = useState("");
  const [disposalOpen, setDisposalOpen] = useState(false);
  const [disposalMode, setDisposalMode] = useState("RETIRE");
  const [disposalSaving, setDisposalSaving] = useState(false);
  const [disposalForm, setDisposalForm] = useState({ reason: "E_WASTE", disposal_date: new Date().toISOString().slice(0, 10), recipient_name: "", sale_amount: "", notes: "", confirmed: false });
  const [disposalFiles, setDisposalFiles] = useState([]);

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetForm, setAssetForm] = useState(emptyAsset);
  const [assetErrors, setAssetErrors] = useState({});
  const [assetSaving, setAssetSaving] = useState(false);
  const [assetEditImages, setAssetEditImages] = useState([]);
  const [assetEditImagePreviews, setAssetEditImagePreviews] = useState([]);
  const [assetImageRemoving, setAssetImageRemoving] = useState("");
  const [assetImageReplacing, setAssetImageReplacing] = useState("");
  const [assetImageReplaceTarget, setAssetImageReplaceTarget] = useState(null);
  const assetImageReplaceInputRef = useRef(null);
  const [assetEditTab, setAssetEditTab] = useState(0);
  const [custodyTransferForm, setCustodyTransferForm] = useState({
    asset_custodian: "",
    asset_custodian_email: "",
    asset_custodian_contact: "",
  });
  const [custodyTransferSaving, setCustodyTransferSaving] = useState(false);

  // map of devices for assign dialog
  const [devicesForAssign, setDevicesForAssign] = useState([]);
  const devicesNeedingReview = devicesForAssign.filter(device => device.asset_match_status === "NEEDS_REVIEW");
  const [deviceAssignQ, setDeviceAssignQ] = useState("");

  // row menu
  const [rowMenu, setRowMenu] = useState({ anchor: null, asset: null });
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteContext, setDeleteContext] = useState({ mode: null, asset: null }); // mode: 'single' | 'bulk'
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });


  const missingOwnerEmailMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("view") === "missing-owner-email";
  }, [location.search]);

  const IGNORED_HISTORY_FIELDS = new Set([
    "updatedat",
    "updated_at",
    "createdat",
    "created_at",
    "qr_code_payload",
    "qr_code_url",
  ]);

  const prettifyField = (key) => {
    const map = {
      asset_custodian: "Asset Custodian",
      asset_custodian_email: "Custodian Email",
      asset_custodian_contact: "Custodian Contact",
      owner_name: "Owner Name",
      owner_email: "Owner Email",
      owner_contact: "Owner Contact",
      department: "Department",
      asset_status: "Asset Status",
      asset_type: "Asset Type",
      delivery_status: "Delivery Status",
      location: "Location",
      vendor_name: "Vendor",
      purchase_date: "Purchase Date",
      purchase_price: "Purchase Price",
      quantity: "Quantity",
      device_id: "Assigned Device",
      warranty_start: "Warranty Start",
      warranty_end: "Warranty End",
      license_start: "License Start",
      license_end: "License End",
    };
    if (map[key]) return map[key];
    return String(key || "")
      .split("_")
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || trimmed.toLowerCase() === "none" || trimmed === "-") return "-";
      return trimmed;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? "-" : value.toLocaleString();
    }
    if (typeof value === "object") {
      return "Updated";
    }
    return String(value);
  };

  const formatTimelineTime = (dateLike) => {
    if (!dateLike) return "-";
    const dt = new Date(dateLike);
    if (Number.isNaN(dt.getTime())) return "-";
    const now = new Date();
    const diffMs = now.getTime() - dt.getTime();
    const absDiffMs = Math.abs(diffMs);
    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    const sevenDays = 7 * oneDay;

    if (absDiffMs < sevenDays) {
      const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
      if (absDiffMs < oneHour) {
        const mins = Math.max(1, Math.round(diffMs / (60 * 1000)));
        return `${rtf.format(-mins, "minute")} (${dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})`;
      }
      if (absDiffMs < oneDay) {
        const hrs = Math.max(1, Math.round(diffMs / oneHour));
        return `${rtf.format(-hrs, "hour")} (${dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})`;
      }
      const days = Math.max(1, Math.round(diffMs / oneDay));
      return `${rtf.format(-days, "day")} (${dt.toLocaleDateString([], { day: "2-digit", month: "short" })})`;
    }

    return dt.toLocaleString([], {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const normalizeHistoryValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || trimmed === "None" || trimmed === "-") return "";
      return trimmed;
    }
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const getMeaningfulChanges = (event) => {
    const changed = event?.details?.changed;
    if (!changed || typeof changed !== "object") return [];
    return Object.entries(changed)
      .filter(([field, delta]) => {
        if (IGNORED_HISTORY_FIELDS.has(String(field || "").toLowerCase())) return false;
        const before = normalizeHistoryValue(delta?.before);
        const after = normalizeHistoryValue(delta?.after);
        return before !== after;
      })
      .map(([field, delta]) => ({
        field,
        label: prettifyField(field),
        before: formatFieldValue(delta?.before),
        after: formatFieldValue(delta?.after),
      }));
  };

  const historyTimelineItems = useMemo(() => {
    const sorted = [...assetHistoryRows].sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return aTime - bTime;
    });

    return sorted
      .map((ev) => {
        const changes = getMeaningfulChanges(ev);
        const count = changes.length;
        const summarizeCustodian = (value) => {
          if (!value || typeof value !== "object") return formatFieldValue(value);
          const name = formatFieldValue(value.asset_custodian);
          const bits = [];
          if (value.asset_custodian_email) bits.push(value.asset_custodian_email);
          if (value.asset_custodian_contact) bits.push(value.asset_custodian_contact);
          return bits.length ? `${name} (${bits.join(", ")})` : name;
        };

        if (ev.action === "asset_custody_transferred") {
          const fromCustodian = summarizeCustodian(ev?.details?.from_asset_custodian);
          const toCustodian = summarizeCustodian(ev?.details?.to_asset_custodian);
          return {
            id: ev.id,
            action: ev.action,
            created_at: ev.created_at,
            headline: "Custody transferred",
            details: `${fromCustodian} -> ${toCustodian}`,
          };
        }

        if (ev.action === "asset_updated" && count === 0) return null;

        if (ev.action === "asset_updated") {
          const custodianChange = changes.find((c) => c.field === "asset_custodian");
          const custodianEmailChange = changes.find((c) => c.field === "asset_custodian_email");
          const custodianContactChange = changes.find((c) => c.field === "asset_custodian_contact");
          if (custodianChange || custodianEmailChange || custodianContactChange) {
            const beforeName = custodianChange?.before || selectedAsset?.asset_custodian || "Unassigned";
            const afterName = custodianChange?.after || beforeName;
            const beforeBits = [
              custodianEmailChange?.before ? `Email: ${custodianEmailChange.before}` : "",
              custodianContactChange?.before ? `Contact: ${custodianContactChange.before}` : "",
            ].filter(Boolean).join(" | ");
            const afterBits = [
              custodianEmailChange?.after ? `Email: ${custodianEmailChange.after}` : "",
              custodianContactChange?.after ? `Contact: ${custodianContactChange.after}` : "",
            ].filter(Boolean).join(" | ");
            return {
              id: ev.id,
              action: ev.action,
              created_at: ev.created_at,
              headline: custodianChange ? "Custody transferred" : "Custodian details updated",
              details: `${beforeName}${beforeBits ? ` (${beforeBits})` : ""} -> ${afterName}${afterBits ? ` (${afterBits})` : ""}`,
            };
          }

          const ownerChange = changes.find((c) => c.field === "owner_name");
          if (ownerChange) {
            return {
              id: ev.id,
              action: ev.action,
              created_at: ev.created_at,
              headline: "Ownership transferred",
              details: `${ownerChange.before} -> ${ownerChange.after}`,
            };
          }

          const deptChange = changes.find((c) => c.field === "department");
          if (deptChange) {
            return {
              id: ev.id,
              action: ev.action,
              created_at: ev.created_at,
              headline: "Department changed",
              details: `${deptChange.before} -> ${deptChange.after}`,
            };
          }

          const headline =
            count === 1
              ? `${changes[0].label} changed`
              : `${count} fields updated`;
          const details =
            count === 1
              ? `from ${changes[0].before} to ${changes[0].after}`
              : changes.slice(0, 3).map((c) => `${c.label}: ${c.before} -> ${c.after}`).join(" | ");
          return {
            id: ev.id,
            action: ev.action,
            created_at: ev.created_at,
            headline,
            details,
          };
        }

        if (ev.action === "asset_assigned") {
          const toHost = ev?.details?.to_device_hostname ? ` (${ev.details.to_device_hostname})` : "";
          return {
            id: ev.id,
            action: ev.action,
            created_at: ev.created_at,
            headline: "Asset assigned",
            details: `device ${formatFieldValue(ev?.details?.from_device_id)} -> ${formatFieldValue(ev?.details?.to_device_id)}${toHost}`,
          };
        }

        if (ev.action === "asset_unassigned") {
          return {
            id: ev.id,
            action: ev.action,
            created_at: ev.created_at,
            headline: "Asset unassigned",
            details: `removed from device ${formatFieldValue(ev?.details?.from_device_id)}`,
          };
        }

        if (ev.action === "asset_created") {
          return {
            id: ev.id,
            action: ev.action,
            created_at: ev.created_at,
            headline: "Asset created",
            details: selectedAsset?.asset_uid ? `Asset ID ${selectedAsset.asset_uid}` : "",
          };
        }

        if (ev.action === "asset_deleted") {
          return {
            id: ev.id,
            action: ev.action,
            created_at: ev.created_at,
            headline: "Asset deleted",
            details: "",
          };
        }

        if (ev.action === "asset_qr_scanned") {
          return {
            id: ev.id,
            action: ev.action,
            created_at: ev.created_at,
            headline: "QR code scanned",
            details: "Public asset details opened from the QR label.",
          };
        }

        return {
          id: ev.id,
          action: ev.action,
          created_at: ev.created_at,
          headline: String(ev.action || "asset event").replaceAll("_", " "),
          details: "",
        };
      })
      .filter(Boolean);
  }, [assetHistoryRows, selectedAsset]);

  const filteredHistoryTimelineItems = useMemo(() => {
    const query = assetHistoryQuery.trim().toLowerCase();
    const from = assetHistoryFrom ? new Date(`${assetHistoryFrom}T00:00:00`) : null;
    const to = assetHistoryTo ? new Date(`${assetHistoryTo}T23:59:59.999`) : null;

    return historyTimelineItems.filter((item) => {
      const occurredAt = new Date(item.created_at);
      if (from && (!Number.isFinite(occurredAt.getTime()) || occurredAt < from)) return false;
      if (to && (!Number.isFinite(occurredAt.getTime()) || occurredAt > to)) return false;
      if (!query) return true;
      return [item.headline, item.details, item.action, selectedAsset?.asset_uid]
        .some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [assetHistoryFrom, assetHistoryQuery, assetHistoryTo, historyTimelineItems, selectedAsset?.asset_uid]);

  async function openAssetHistory(asset) {
    if (!asset) return;
    setSelectedAsset(asset);
    setAssetHistoryOpen(true);
    setAssetHistoryLoading(true);
    setAssetHistoryRows([]);
    setAssetHistoryQuery("");
    setAssetHistoryFrom("");
    setAssetHistoryTo("");
    try {
      const response = await ReportsAPI.assetHistory({
        limit: 200,
        asset_uid: asset.asset_uid,
        asset_id: asset.id,
      });
      setAssetHistoryRows(response?.data?.data || []);
    } catch (e) {
      setSnackbar({
        open: true,
        severity: "error",
        message: e?.response?.data?.error || "Failed to load asset history",
      });
    } finally {
      setAssetHistoryLoading(false);
    }
  }

  // brand options
  const brandOptions = useMemo(() => {
    const s = new Set();
    assets.forEach(a => {
      const v = (a.vendor_name || "").trim();
      if (v) s.add(v);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  // asset type options - dynamic from existing assets
  const assetTypeOptions = useMemo(() => {
    const s = new Set();
    COMMON_ASSET_TYPE_SUGGESTIONS.forEach((type) => {
      const normalized = normalizeAssetTypeValue(type);
      if (normalized) s.add(normalized);
    });
    assets.forEach(a => {
      const t = normalizeAssetTypeValue(a.asset_type);
      if (!t) return;
      s.add(t);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  // load assets + devices
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setAssetsLoading(true);
        const res = await AssetsAPI.list();
        if (!mounted) return;
        setAssets(res?.data?.data || []);
      } finally {
        if (mounted) setAssetsLoading(false);
      }
    })();
    (async () => {
      try {
        const response = await AssetCustomFieldsAPI.list();
        if (!mounted) return;
        setAssetCustomFields(response?.data?.data || []);
      } catch {
        if (mounted) setAssetCustomFields([]);
      }
    })();
    (async () => {
      try {
        const res = await EnterprisesAPI.getMyEnterprise();
        if (!mounted) return;
        setEnterprisePlan(res?.data?.data || null);
      } catch {
        if (mounted) setEnterprisePlan(null);
      }
    })();
    (async () => {
      try {
        const r = await DevicesAPI.list();
        if (!mounted) return;
        setDevicesForAssign(r?.data?.data || []);
      } catch {}
    })();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const filteredAssets = useMemo(() => {
    let list = [...assets];
    if (missingOwnerEmailMode) {
      list = list.filter((asset) => !String(asset.owner_email || "").trim());
    }
    const s = assetQ.trim().toLowerCase();
    if (s) {
      list = list.filter(a =>
        String(a.asset_uid || a.id).toLowerCase().includes(s) ||
        String(a.owner_name || "").toLowerCase().includes(s) ||
        String(a.asset_custodian || "").toLowerCase().includes(s) ||
        String(a.asset_custodian_email || "").toLowerCase().includes(s) ||
        String(a.asset_custodian_contact || "").toLowerCase().includes(s) ||
        String(a.department || "").toLowerCase().includes(s)
      );
    }
    if (assetTypeFilter !== "ALL") {
      list = list.filter(a => String(a.asset_type || "").toUpperCase() === assetTypeFilter);
    }
    if (sourceFilter !== "ALL") {
      list = list.filter(a => String(a.record_source || "LEGACY_UNKNOWN") === sourceFilter);
    }
    if (deliveryFilter !== "ALL") {
      list = list.filter(a => String(a.delivery_status || "").toUpperCase() === deliveryFilter);
    }
    if (lifecycleFilter === "ACTIVE") {
      list = list.filter((asset) => !["RETIRED", "DISPOSED", "SCRAP"].includes(String(asset.asset_status || "").toUpperCase()));
    } else if (lifecycleFilter !== "ALL") {
      list = list.filter((asset) => String(asset.asset_status || "").toUpperCase() === lifecycleFilter);
    }
    const { by, dir } = assetSort;
    list.sort((a, b) => {
      const d = dir === "asc" ? 1 : -1;
      const valueFor = (asset) => {
        if (by === "purchase_price") return Number(asset.purchase_price || 0);
        if (by === "purchase_date" || by === "warranty_end") return asset[by] ? new Date(asset[by]).getTime() : 0;
        if (by === "last_scanned_at") return asset.last_scanned_at ? new Date(asset.last_scanned_at).getTime() : 0;
        if (by === "owner_name") return `${asset.owner_name || ""} ${asset.asset_custodian || ""}`;
        return String(asset[by] || "");
      };
      const left = valueFor(a);
      const right = valueFor(b);
      if (typeof left === "number" && typeof right === "number") return (left - right) * d;
      return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: "base" }) * d;
    });
    return list;
  }, [assets, assetQ, assetSort, assetTypeFilter, deliveryFilter, lifecycleFilter, missingOwnerEmailMode, sourceFilter]);

  const requestAssetSort = (by) => {
    setAssetSort((current) => ({
      by,
      dir: current.by === by && current.dir === "asc" ? "desc" : "asc",
    }));
  };

  const filteredDevicesForAssign = useMemo(() => {
    const q = deviceAssignQ.trim().toLowerCase();
    if (!q) return devicesForAssign;
    return devicesForAssign.filter((d) => {
      const hostname = String(d?.hostname || "").toLowerCase();
      const uuid = String(d?.device_uuid || "").toLowerCase();
      const deviceId = String(d?.device_id || "").toLowerCase();
      const serial = String(d?.serial || "").toLowerCase();
      return (
        hostname.includes(q) ||
        uuid.includes(q) ||
        deviceId.includes(q) ||
        serial.includes(q)
      );
    });
  }, [devicesForAssign, deviceAssignQ]);

  const assetMediaLocked = String(enterprisePlan?.user_status || "").toLowerCase() === "free";
  const assetImageCount = Array.isArray(selectedAsset?.details?.asset_images) ? selectedAsset.details.asset_images.length : 0;
  const remainingAssetImageSlots = Math.max(0, MAX_ASSET_IMAGE_COUNT - assetImageCount);

  useEffect(() => {
    return () => {
      assetEditImagePreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [assetEditImagePreviews]);

  function validateAsset(f, allowIncomplete = false) {
    const errs = {};
    if (!allowIncomplete && !f.owner_name?.trim()) errs.owner_name = "Asset owner is required";
    if (!f.asset_type?.trim()) errs.asset_type = "Asset type is required";
    if (!allowIncomplete && !f.owner_email?.trim()) {
      errs.owner_email = "Asset owner email is required";
    } else if (f.owner_email?.trim() && !isValidEmail(f.owner_email)) {
      errs.owner_email = "Enter a valid email address";
    }
    if (f.asset_custodian_email?.trim() && !isValidEmail(f.asset_custodian_email)) {
      errs.asset_custodian_email = "Enter a valid email address";
    }
    if (!allowIncomplete && !f.department?.trim()) errs.department = "Department is required";
    if (!allowIncomplete && !f.purchase_date) errs.purchase_date = "Purchase date is required";
    if (!allowIncomplete && (f.purchase_price === "" || f.purchase_price == null)) errs.purchase_price = "Price is required";
    if (f.asset_status && !BACKEND_ASSET_STATUS.includes(String(f.asset_status).toUpperCase())) {
      errs.asset_status = "Select a valid asset status";
    }
    if (f.delivery_status && !BACKEND_DELIVERY_STATUS.includes(String(f.delivery_status).toUpperCase())) {
      errs.delivery_status = "Select a valid delivery status";
    }
    if (f.quantity != null && Number(f.quantity) <= 0) {
      errs.quantity = "Quantity must be greater than 0";
    }
    if (f.useful_life_months !== "" && f.useful_life_months != null && Number(f.useful_life_months) <= 0) {
      errs.useful_life_months = "Useful life must be greater than 0";
    }
    if (f.depreciation_rate_percent !== "" && f.depreciation_rate_percent != null && Number(f.depreciation_rate_percent) < 0) {
      errs.depreciation_rate_percent = "Depreciation rate cannot be negative";
    }
    if (f.salvage_value !== "" && f.salvage_value != null && Number(f.salvage_value) < 0) {
      errs.salvage_value = "Salvage value cannot be negative";
    }
    if (f.depreciation_method && !DEPRECIATION_METHOD_OPTIONS.some(opt => opt.value === String(f.depreciation_method).toUpperCase())) {
      errs.depreciation_method = "Select a valid depreciation method";
    }
    if (f.depreciation_start_mode && !DEPRECIATION_START_MODE_OPTIONS.some(opt => opt.value === String(f.depreciation_start_mode).toUpperCase())) {
      errs.depreciation_start_mode = "Select a valid start mode";
    }
    if ((f.remarks || "").length > 250) errs.remarks = "Max 250 characters";
    return errs;
  }

  async function submitCreate(resetAfter = false) {
    const errs = validateAsset(assetForm);
    if (Object.keys(errs).length) {
      setAssetErrors(errs);
      return;
    }
    const payload = normalizeAssetPayload({ ...assetForm });
    const currency = String(payload.purchase_currency || "INR").toUpperCase();
    payload.details = buildDepreciationDetails({ ...payload, purchase_currency: PURCHASE_CURRENCY_OPTIONS.includes(currency) ? currency : "INR" });
    delete payload.purchase_currency;
    delete payload.depreciation_method;
    delete payload.useful_life_months;
    delete payload.depreciation_rate_percent;
    delete payload.salvage_value;
    delete payload.depreciation_start_mode;
    Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);
    payload.asset_type = normalizeAssetTypeValue(payload.asset_type);
    if (!payload.quantity) payload.quantity = 1;

    try {
      setAssetSaving(true);
      await AssetsAPI.create(payload);
      setAssetSaving(false);
      if (resetAfter) {
        setAssetForm(emptyAsset);
        setAssetErrors({});
        setAssetEditTab(0);
      } else {
        setAssetCreateOpen(false);
      }
      setSnackbar({ open: true, severity: "success", message: "Asset created successfully." });
      setRefreshKey(k => k + 1);
    } catch (e) {
      setAssetSaving(false);
      console.error(e);
      setSnackbar({ open: true, severity: "error", message: getAssetErrorMessage(e) });
    }
  }

  async function submitAssign(deviceId) {
    try {
      // Pass "null" string for unassign, or the actual device ID
      const targetId = deviceId === "" || deviceId === null ? "null" : deviceId;
      await AssetsAPI.assignToDevice(selectedAsset.id, targetId);
      setAssetAssignOpen(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e?.message || "Failed to assign asset");
    }
  }

  function openCustodyTransferDialog() {
    if (!selectedAsset) return;
    setCustodyTransferForm({
      asset_custodian: selectedAsset.asset_custodian || "",
      asset_custodian_email: selectedAsset.asset_custodian_email || "",
      asset_custodian_contact: selectedAsset.asset_custodian_contact || "",
    });
    setCustodyTransferOpen(true);
  }

  async function submitCustodyTransfer() {
    const nextName = String(custodyTransferForm.asset_custodian || "").trim();
    const nextEmail = String(custodyTransferForm.asset_custodian_email || "").trim();
    const nextContact = String(custodyTransferForm.asset_custodian_contact || "").trim();
    if (!nextName) {
      setSnackbar({ open: true, severity: "error", message: "Asset custodian is required." });
      return;
    }

    try {
      setCustodyTransferSaving(true);
      const response = await AssetsAPI.transferCustody(selectedAsset.id, {
        asset_custodian: nextName,
        asset_custodian_email: nextEmail,
        asset_custodian_contact: nextContact,
      });
      const updatedAsset = response?.data?.data || null;
      if (updatedAsset) {
        setSelectedAsset((prev) => (prev ? { ...prev, ...updatedAsset } : updatedAsset));
        setAssets((prev) => prev.map((asset) => (asset.id === updatedAsset.id ? { ...asset, ...updatedAsset } : asset)));
        if (assetHistoryOpen) {
          const historyResponse = await ReportsAPI.assetHistory({
            limit: 200,
            asset_uid: updatedAsset.asset_uid,
            asset_id: updatedAsset.id,
          });
          setAssetHistoryRows(historyResponse?.data?.data || []);
        }

      }
      setCustodyTransferOpen(false);
      setRefreshKey((k) => k + 1);
      setSnackbar({ open: true, severity: "success", message: "Custody transferred successfully." });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, severity: "error", message: e?.response?.data?.error || e?.message || "Failed to transfer custody" });
    } finally {
      setCustodyTransferSaving(false);
    }
  }

  const openDisposal = (mode) => {
    setDisposalMode(mode);
    setDisposalForm({ reason: "E_WASTE", disposal_date: new Date().toISOString().slice(0, 10), recipient_name: "", sale_amount: "", notes: "", confirmed: false });
    setDisposalFiles([]);
    setDisposalOpen(true);
  };

  const submitDisposal = async () => {
    if (!selectedAsset) return;
    if (!disposalForm.confirmed) {
      setSnackbar({ open: true, severity: "error", message: "Confirm that this asset must leave active inventory." });
      return;
    }
    setDisposalSaving(true);
    try {
      const payload = {
        reason: disposalForm.reason,
        disposal_date: disposalForm.disposal_date,
        recipient_name: disposalForm.recipient_name,
        sale_amount: disposalMode === "DISPOSE" ? disposalForm.sale_amount : undefined,
        notes: disposalForm.notes,
      };
      const response = disposalMode === "DISPOSE"
        ? await AssetsAPI.dispose(selectedAsset.id, payload)
        : await AssetsAPI.retire(selectedAsset.id, payload);
      if (disposalFiles.length) await AssetsAPI.uploadDisposalEvidence(selectedAsset.id, disposalFiles);
      const updated = { ...selectedAsset, asset_status: disposalMode === "DISPOSE" ? "DISPOSED" : "RETIRED", device_id: null, disposal: response?.data?.data || selectedAsset.disposal };
      setSelectedAsset(updated);
      setAssets((current) => current.map((asset) => asset.id === updated.id ? { ...asset, ...updated } : asset));
      setDisposalOpen(false);
      setRefreshKey((key) => key + 1);
      setSnackbar({ open: true, severity: "success", message: disposalMode === "DISPOSE" ? "Asset disposed and removed from active inventory." : "Asset retired and removed from active inventory." });
    } catch (error) {
      setSnackbar({ open: true, severity: "error", message: error?.response?.data?.error || "Failed to update asset lifecycle." });
    } finally {
      setDisposalSaving(false);
    }
  };

  const downloadDisposalCertificate = async () => {
    if (!selectedAsset) return;
    try {
      const response = await AssetsAPI.downloadDisposalCertificate(selectedAsset.id);
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedAsset.asset_uid || "asset"}-disposal-certificate.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setSnackbar({ open: true, severity: "error", message: error?.response?.data?.error || "Could not download disposal certificate." });
    }
  };

  async function submitEdit() {
    const errs = validateAsset(assetForm, selectedAsset?.record_source === "AGENT");
    if (Object.keys(errs).length) {
      setAssetErrors(errs);
      return;
    }
    const existingImages = Array.isArray(selectedAsset?.details?.asset_images) ? selectedAsset.details.asset_images.length : 0;
    if (assetEditImages.some((file) => file.size > MAX_ASSET_IMAGE_SIZE_BYTES)) {
      setAssetErrors((prev) => ({ ...prev, asset_images: "Each image must be smaller than 2 MB." }));
      return;
    }
    if (existingImages >= MAX_ASSET_IMAGE_COUNT) {
      setAssetErrors((prev) => ({ ...prev, asset_images: "Photo limit reached" }));
      return;
    }
    if (assetEditImages.length > remainingAssetImageSlots) {
      setAssetErrors((prev) => ({ ...prev, asset_images: `You can add only ${remainingAssetImageSlots} more image${remainingAssetImageSlots === 1 ? "" : "s"}.` }));
      return;
    }
    const payload = normalizeAssetPayload({ ...assetForm });
    const currency = String(payload.purchase_currency || "INR").toUpperCase();
    payload.details = buildDepreciationDetails({ ...payload, purchase_currency: PURCHASE_CURRENCY_OPTIONS.includes(currency) ? currency : "INR" });
    delete payload.purchase_currency;
    delete payload.depreciation_method;
    delete payload.useful_life_months;
    delete payload.depreciation_rate_percent;
    delete payload.salvage_value;
    delete payload.depreciation_start_mode;
    Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);

    try {
      setAssetSaving(true);
      await AssetsAPI.update(selectedAsset.id, payload);
      if (assetEditImages.length) {
        const formData = new FormData();
        assetEditImages.slice(0, remainingAssetImageSlots).forEach((file) => formData.append("images", file));
        await AssetsAPI.uploadImages(selectedAsset.id, formData);
      }
      setAssetSaving(false);
      setAssetEditOpen(false);
      setAssetEditImages([]);
      setAssetEditImagePreviews([]);
      setSnackbar({
        open: true,
        severity: "success",
        message: assetEditImages.length ? "Image uploaded successfully." : "Asset updated successfully.",
      });
      setRefreshKey(k => k + 1);
    } catch (e) {
      setAssetSaving(false);
      console.error(e);
      setSnackbar({ open: true, severity: "error", message: getAssetErrorMessage(e) });
    }
  }

  async function removeAssetImage(item) {
    const blobName = item?.blob_name || item?.blobName || "";
    const url = item?.url || "";
    if (!blobName && !url) return;
    if (!window.confirm("Remove this asset photo?")) return;

    try {
      setAssetImageRemoving(blobName || url);
      const response = await AssetsAPI.removeImage(selectedAsset.id, blobName ? { blob_name: blobName } : { url });
      const nextImages = response?.data?.data?.asset_images || [];
      setSelectedAsset((prev) =>
        prev
          ? {
              ...prev,
              details: {
                ...(prev.details || {}),
                asset_images: nextImages,
              },
            }
          : prev
      );
      setSnackbar({ open: true, severity: "success", message: "Asset photo removed." });
      setRefreshKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, severity: "error", message: e?.response?.data?.error || e?.message || "Failed to remove photo" });
    } finally {
      setAssetImageRemoving("");
    }
  }

  function startReplaceAssetImage(item) {
    setAssetImageReplaceTarget(item);
    if (assetImageReplaceInputRef.current) {
      assetImageReplaceInputRef.current.value = "";
      assetImageReplaceInputRef.current.click();
    }
  }

  async function handleAssetImageReplaceChange(event) {
    const file = event.target.files?.[0];
    const target = assetImageReplaceTarget;
    event.target.value = "";

    if (!file || !target) {
      setAssetImageReplaceTarget(null);
      return;
    }
    if (file.size > MAX_ASSET_IMAGE_SIZE_BYTES) {
      setSnackbar({ open: true, severity: "error", message: "Each image must be smaller than 2 MB." });
      setAssetImageReplaceTarget(null);
      return;
    }

    const blobName = target?.blob_name || target?.blobName || "";
    const url = target?.url || "";
    const index = Array.isArray(selectedAsset?.details?.asset_images)
      ? selectedAsset.details.asset_images.findIndex((item) => {
          const itemBlob = item?.blob_name || item?.blobName || "";
          const itemUrl = item?.url || "";
          return (blobName && String(itemBlob).trim() === String(blobName).trim())
            || (!blobName && url && String(itemUrl).trim() === String(url).trim());
        })
      : -1;

    if (index < 0) {
      setSnackbar({ open: true, severity: "error", message: "Asset photo not found." });
      setAssetImageReplaceTarget(null);
      return;
    }

    try {
      setAssetImageReplacing(blobName || url || String(index));
      const formData = new FormData();
      formData.append("image", file);
      if (blobName) formData.append("blob_name", blobName);
      else if (url) formData.append("url", url);
      else formData.append("index", String(index));

      const response = await AssetsAPI.replaceImage(selectedAsset.id, formData);
      const nextImages = response?.data?.data?.asset_images || [];
      setSelectedAsset((prev) =>
        prev
          ? {
              ...prev,
              details: {
                ...(prev.details || {}),
                asset_images: nextImages,
              },
          }
          : prev
      );
      setSnackbar({ open: true, severity: "success", message: "Image uploaded successfully." });
      setRefreshKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, severity: "error", message: e?.response?.data?.error || e?.message || "Failed to replace photo" });
    } finally {
      setAssetImageReplacing("");
      setAssetImageReplaceTarget(null);
    }
  }

  function exportAssetsCSV(rows = []) {
    const columns = [
      { header: "ID", key: "id" },
      { header: "Asset UID", key: "asset_uid" },
      { header: "Owner", key: "owner_name" },
      { header: "Custodian", key: "asset_custodian" },
      { header: "Department", key: "department" },
      { header: "Asset Type", key: "asset_type" },
      { header: "Vendor", key: "vendor_name" },
      { header: "Purchase Date", get: r => fmtDate(r.purchase_date) },
      { header: "Currency", get: r => getAssetCurrency(r) },
      { header: "Price", key: "purchase_price" },
      { header: "Delivery Status", key: "delivery_status" },
      { header: "Asset Status", key: "asset_status" },
      { header: "Source", get: r => assetSourceLabel(r.record_source) },
      { header: "Warranty End", get: r => fmtDate(r.warranty_end) },
    ];
    const csv = toCSV(rows.length ? rows : assets, columns);
    downloadCSV(csv, `assets_${new Date().toISOString().slice(0, 10)}.csv`);
  }

  // helpers for status chip
  const daysLeft = dateStr => {
    if (!dateStr) return null;
    const end = new Date(dateStr);
    if (isNaN(end)) return null;
    const diff = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  const statusChip = (days) => {
    if (days == null) return <Chip size="small" label="No warranty" variant="outlined" sx={{ bgcolor: "#F8FAFC", borderColor: "#CBD5E1", color: "#64748B" }} />;
    let bg = "#F1F5F9";
    let fg = "#475569";
    let text = "";
    if (days < 0) {
      bg = "#FEE2E2";
      fg = "#991B1B";
      text = "Warranty expired";
    } else if (days <= 30) {
      bg = "#FEF3C7";
      fg = "#92400E";
      text = `Expiring soon (${days}d)`;
    } else {
      bg = "#DCFCE7";
      fg = "#166534";
      text = "Warranty OK";
    }
    return <Chip size="small" label={text} variant="filled" sx={{ bgcolor: bg, color: fg, fontWeight: 600 }} />;
  };

  // aggregated stats for stat cards
  const assetStats = useMemo(() => {
    const hardwareTypes = new Set(["RAM", "SSD", "HDD", "GPU", "PERIPHERAL"]);
    const activeAssets = assets.filter((asset) => !["RETIRED", "DISPOSED", "SCRAP"].includes(String(asset.asset_status || "").toUpperCase()));
    let total = activeAssets.length;
    let assigned = 0;
    let unassigned = 0;
    let devices = 0;
    let hardware = 0;
    let software = 0;
    let expiring30 = 0;
    let pendingDelivery = 0;
    activeAssets.forEach(a => {
      if (a.device_id) assigned++; else unassigned++;
      if (a.asset_type === "DEVICE") devices++;
      if (hardwareTypes.has(a.asset_type)) hardware++;
      if (["SOFTWARE", "LICENSE"].includes(a.asset_type)) software++;
      const d = daysLeft(a.warranty_end);
      if (d != null && d >= 0 && d <= 30) expiring30++;
      if (a.delivery_status === "ORDERED" || a.delivery_status === "DISPATCHED") pendingDelivery++;
    });
    return { total, assigned, unassigned, devices, hardware, software, expiring30, pendingDelivery };
  }, [assets]);

  return (
    <Box sx={{ width: '100%', py: { xs: 1.5, sm: 2.2, md: 3.4 }, px: { xs: 1.2, sm: 1.8, md: 3 } }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" mb={{ xs: 1.5, sm: 2.2, md: 3 }} gap={{ xs: 1.2, sm: 2 }} sx={sectionMotion(0)}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.1rem', sm: '1.35rem', md: '1.55rem' } }}>Manage Assets</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.74rem', sm: '0.86rem' } }}>
            Centralized asset register with assignment, lifecycle, and warranty tracking.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Tooltip title="Refresh Assets"><IconButton onClick={() => setRefreshKey(k => k + 1)}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="outlined" onClick={() => exportAssetsCSV(filteredAssets)} startIcon={<DownloadIcon />}>Export CSV</Button>
          <Button variant="outlined" onClick={() => setBulkOpen(true)} startIcon={<UploadFile />}>Bulk Upload</Button>
          <Button variant="outlined" onClick={() => setAssetCustomFieldsOpen(true)}>Custom fields</Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={selectedIds.length === 0}
            onClick={() => {
              if (!selectedIds.length) return;
              setDeleteContext({ mode: 'bulk', asset: null });
              setDeleteDialogOpen(true);
            }}
          >
            Delete Selected
          </Button>
          <Button variant="contained" onClick={() => { setAssetForm(emptyAsset); setAssetErrors({}); setAssetEditTab(0); setAssetCreateOpen(true); }} startIcon={<Add />}>Add Asset</Button>
        </Stack>
      </Box>

      {/* User-seat plan summary. Asset records are not capped by plan. */}
      {enterprisePlan && (
        <Paper
          sx={{
            ...sectionMotion(70),
            mb: { xs: 1.6, sm: 2.3, md: 3 },
            p: { xs: 1.4, sm: 2.1, md: 2.5 },
            background: '#FFFFFF',
            color: '#0F172A',
            border: '1px solid #E2E8F0',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {(enterprisePlan.user_count || 0) >= (enterprisePlan.user_seat_limit || 1) ? (
              <WarningAmber sx={{ fontSize: 40 }} />
            ) : (enterprisePlan.user_count || 0) / (enterprisePlan.user_seat_limit || 1) > 0.8 ? (
              <TrendingUp sx={{ fontSize: 40 }} />
            ) : (
              <CheckCircle sx={{ fontSize: 40 }} />
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {enterprisePlan.user_status.toUpperCase()} Plan
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                {enterprisePlan.user_count || 0} / {enterprisePlan.user_seat_limit || 1} named users
                {(enterprisePlan.user_count || 0) >= (enterprisePlan.user_seat_limit || 1) && (
                  <strong> • Limit Reached!</strong>
                )}
                {(enterprisePlan.user_count || 0) / (enterprisePlan.user_seat_limit || 1) > 0.8 &&
                 (enterprisePlan.user_count || 0) < (enterprisePlan.user_seat_limit || 1) && (
                  <strong> • {Math.round((1 - (enterprisePlan.user_count || 0) / (enterprisePlan.user_seat_limit || 1)) * 100)}% seats remaining</strong>
                )}
              </Typography>
              {enterprisePlan.user_status === 'free' && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                  Free includes one named user. Asset records are not capped by plan; upgrade to add users and unlock premium features.
                </Typography>
              )}
            </Box>
          </Box>
          {(enterprisePlan.user_status === 'free' || (enterprisePlan.user_count || 0) >= (enterprisePlan.user_seat_limit || 1)) && (
            <Button
              variant="contained"
              sx={{
                bgcolor: '#0F172A',
                color: '#FFFFFF',
                fontWeight: 700,
                px: 3,
                '&:hover': {
                  bgcolor: '#1E293B',
                },
              }}
              onClick={() => {
                navigate(ROUTES.PRICING);
              }}
            >
              {(enterprisePlan.user_count || 0) >= (enterprisePlan.user_seat_limit || 1)
                ? 'Upgrade Now' 
                : 'View Plans'}
            </Button>
          )}
        </Paper>
      )}

      {/* Stat Cards */}
      <Grid container spacing={{ xs: 1.1, sm: 1.5, md: 2 }} sx={{ ...sectionMotion(130), mb: { xs: 1.8, sm: 2.4, md: 3 } }}>
        <Grid item xs={4} sm={4} md={2}><StatCard label="Total" value={assetStats.total} icon={<ListAltIcon />} /></Grid>
        <Grid item xs={4} sm={4} md={2}><StatCard label="Assigned" value={assetStats.assigned} icon={<AssignmentTurnedInIcon />} /></Grid>
        <Grid item xs={4} sm={4} md={2}><StatCard label="Unassigned" value={assetStats.unassigned} icon={<HelpCenterIcon />} /></Grid>
        <Grid item xs={4} sm={4} md={2}><StatCard label="Pending Delivery" value={assetStats.pendingDelivery} icon={<DevicesOtherIcon />} color="warning" /></Grid>
        <Grid item xs={4} sm={4} md={2}><StatCard label="Hardware" value={assetStats.hardware} icon={<MemoryIcon />} /></Grid>
        <Grid item xs={4} sm={4} md={2}><StatCard label="Software" value={assetStats.software} icon={<AppsIcon />} /></Grid>
      </Grid>

      {/* Full-screen Table Card */}
      <Paper sx={t => ({
        ...sectionMotion(200),
        p: { xs: 1.1, sm: 1.6, md: 2 },
        borderRadius: 2,
        height: { xs: 'calc(100vh - 210px)', sm: 'calc(100vh - 220px)' },
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: t.palette.background.paper,
        border: `1px solid ${t.palette.divider}`
      })}>
        <Stack direction="row" spacing={{ xs: 1.1, sm: 1.6, md: 2 }} mb={{ xs: 1.1, sm: 1.8, md: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search id / owner / dept"
            value={assetQ}
            onChange={e => setAssetQ(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            sx={{ minWidth: { xs: '100%', sm: 240 } }}
          />
          <Select
            size="small"
            value={assetTypeFilter}
            onChange={e => setAssetTypeFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 160 } }}
            disableScrollLock
          >
            <MenuItem value="ALL">All Types</MenuItem>
            {assetTypeOptions.map(type => (
              <MenuItem key={type} value={type}>{humanizeAssetTypeLabel(type)}</MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={deliveryFilter}
            onChange={e => setDeliveryFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 180 } }}
            disableScrollLock
          >
            <MenuItem value="ALL">All Delivery Status</MenuItem>
            <MenuItem value="ORDERED">Ordered</MenuItem>
            <MenuItem value="DISPATCHED">Dispatched</MenuItem>
            <MenuItem value="RECEIVED">Received</MenuItem>
          </Select>
          <Select
            size="small"
            value={lifecycleFilter}
            onChange={e => setLifecycleFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 170 } }}
            disableScrollLock
          >
            <MenuItem value="ACTIVE">Active inventory</MenuItem>
            <MenuItem value="RETIRED">Retired</MenuItem>
            <MenuItem value="DISPOSED">Disposed</MenuItem>
            <MenuItem value="ALL">All lifecycle states</MenuItem>
          </Select>
          <Select
            size="small"
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 180 } }}
            aria-label="Filter assets by source"
          >
            <MenuItem value="ALL">All sources</MenuItem>
            {Object.entries(ASSET_SOURCE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </Select>
        </Stack>
        <Stack direction="row" spacing={1} mb={{ xs: 1, sm: 1.5 }} flexWrap="wrap" useFlexGap>
          {assetTypeFilter !== "ALL" && (
            <Chip
              size="small"
              label={`Type: ${humanizeAssetTypeLabel(assetTypeFilter)}`}
              onDelete={() => setAssetTypeFilter("ALL")}
              sx={{ bgcolor: "#EFF6FF", color: "#1E3A8A", border: "1px solid #BFDBFE" }}
            />
          )}
          {deliveryFilter !== "ALL" && (
            <Chip
              size="small"
              label={`Delivery: ${deliveryFilter}`}
              onDelete={() => setDeliveryFilter("ALL")}
              sx={{ bgcolor: "#ECFDF3", color: "#166534", border: "1px solid #BBF7D0" }}
            />
          )}
          {lifecycleFilter !== "ACTIVE" && (
            <Chip size="small" label={`Lifecycle: ${lifecycleFilter === "ALL" ? "All" : lifecycleFilter.charAt(0) + lifecycleFilter.slice(1).toLowerCase()}`} onDelete={() => setLifecycleFilter("ACTIVE")} sx={{ bgcolor: "#F8FAFC", color: "#334155", border: "1px solid #CBD5E1" }} />
          )}
          {sourceFilter !== "ALL" && <Chip size="small" label={`Source: ${assetSourceLabel(sourceFilter)}`} onDelete={() => setSourceFilter("ALL")} />}
        </Stack>
        {devicesNeedingReview.length > 0 && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {devicesNeedingReview.length} agent {devicesNeedingReview.length === 1 ? "device needs" : "devices need"} asset matching review. Check the serial and any existing links before assigning the correct asset.
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {devicesNeedingReview.slice(0, 3).map(device => `${device.hostname || device.device_uuid || `Device ${device.id}`}: ${device.asset_match_note || "Check the asset link"}`).join(" • ")}
              {devicesNeedingReview.length > 3 ? ` • ${devicesNeedingReview.length - 3} more` : ""}
            </Typography>
          </Alert>
        )}
        {missingOwnerEmailMode && (
          <Alert
            severity="warning"
            sx={{ mb: 1.5, alignItems: "center" }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(ROUTES.ASSETS)}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Show all assets
              </Button>
            }
          >
            Showing assets without an owner email. These records will be skipped by audit notifications until the email is added.
          </Alert>
        )}
        <TableContainer sx={{ flex: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selectedIds.length > 0 && selectedIds.length < filteredAssets.length}
                    checked={filteredAssets.length > 0 && selectedIds.length === filteredAssets.length}
                    onChange={e => {
                      const checked = e.target.checked;
                      if (checked) {
                        setSelectedIds(filteredAssets.map(a => a.id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </TableCell>
                {[
                  ["asset_uid", "Asset ID", 140], ["owner_name", "Owner / Custodian", 200], ["asset_type", "Asset Type", 180], ["record_source", "Source", 170],
                  ["vendor_name", "Vendor", 200], ["purchase_date", "Purchase Date", 140], ["purchase_price", "Price", 120, "right"],
                  ["delivery_status", "Delivery", 150], ["warranty_end", "Warranty", 140], ["last_scanned_at", "QR Scanned", 105],
                ].map(([by, label, width, align]) => (
                  <TableCell key={by} width={width} align={align || "left"} sortDirection={assetSort.by === by ? assetSort.dir : false}>
                    <TableSortLabel active={assetSort.by === by} direction={assetSort.by === by ? assetSort.dir : "asc"} onClick={() => requestAssetSort(by)}>
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assetsLoading ? (
                <TableRow><TableCell colSpan={12}>Loading...</TableCell></TableRow>
              ) : filteredAssets.length ? (
                filteredAssets.map(a => {
                  const checked = selectedIds.includes(a.id);
                  return (
                  <TableRow
                    key={a.id}
                    hover
                    selected={checked}
                    sx={{
                      "&:hover": { bgcolor: "#F8FAFC" },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={checked}
                        onChange={e => {
                          const isChecked = e.target.checked;
                          setSelectedIds(prev => {
                            if (isChecked) {
                              return prev.includes(a.id) ? prev : [...prev, a.id];
                            }
                            return prev.filter(id => id !== a.id);
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>{a.asset_uid || a.id}</TableCell>
                    <TableCell>
                      <Stack spacing={0.1}>
                        <Typography variant="body2" fontWeight={600}>{a.owner_name || "—"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {a.asset_custodian || "Custodian not set"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const meta = getAssetTypeMeta(a.asset_type);
                        return (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                bgcolor: `${meta.color}20`,
                                color: meta.color,
                              }}
                            >
                              {meta.icon}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600}>
                              {meta.label}
                            </Typography>
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={assetSourceLabel(a.record_source)} /></TableCell>
                    <TableCell>{a.vendor_name || "—"}</TableCell>
                    <TableCell>{toDMY(a.purchase_date)}</TableCell>
                    <TableCell align="right">{formatPurchaseAmount(a)}</TableCell>
                    <TableCell>
                      {(() => {
                        const delivery = getDeliveryStatusMeta(a.delivery_status);
                        return (
                          <Chip
                            size="small"
                            label={delivery.label}
                            sx={{
                              bgcolor: delivery.bg,
                              color: delivery.fg,
                              fontWeight: 600,
                              fontSize: "0.75rem",
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {statusChip(daysLeft(a.warranty_end))}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={a.last_scanned_at ? `Last QR scan: ${fmtDateTime(a.last_scanned_at)}` : "QR code has not been scanned yet"}>
                        <Checkbox
                          size="small"
                          checked={Boolean(a.last_scanned_at)}
                          inputProps={{ readOnly: true, "aria-label": a.last_scanned_at ? "QR scanned at least once" : "QR not scanned yet" }}
                          sx={{ color: "#cbd5e1", "&.Mui-checked": { color: "#16a34a" } }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <IconButton size="small" onClick={e => setRowMenu({ anchor: e.currentTarget, asset: a })}><MoreVert /></IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );})
              ) : (
                <TableRow><TableCell colSpan={12}>No assets</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Row menu */}
      <Menu
        anchorEl={rowMenu.anchor}
        open={Boolean(rowMenu.anchor)}
        onClose={() => setRowMenu({ anchor: null, asset: null })}
      >
        <MenuItem onClick={() => {
          const a = rowMenu.asset;
          if (!a) return;
          setSelectedAsset(a);
          setAssetViewOpen(true);
          setRowMenu({ anchor: null, asset: null });
        }}>
          View
        </MenuItem>
        {!['RETIRED', 'DISPOSED', 'SCRAP'].includes(String(rowMenu.asset?.asset_status || '').toUpperCase()) ? <MenuItem onClick={() => {
          const a = rowMenu.asset;
          if (!a) return;
          setSelectedAsset(a);
          setAssetAssignOpen(true);
          setRowMenu({ anchor: null, asset: null });
        }}>
          {rowMenu.asset?.device_id ? "Transfer" : "Assign"}
        </MenuItem> : null}
        <MenuItem onClick={() => {
          const a = rowMenu.asset;
          setRowMenu({ anchor: null, asset: null });
          void openAssetHistory(a);
        }}>
          View History
        </MenuItem>
        <MenuItem onClick={() => {
          const a = rowMenu.asset;
          if (!a) return;
          window.open(getPublicAssetLink(a), "_blank", "noopener,noreferrer");
          setRowMenu({ anchor: null, asset: null });
        }}>
          Open Public Details
        </MenuItem>
        <MenuItem onClick={async () => {
          const a = rowMenu.asset;
          if (!a) return;
          try {
            await navigator.clipboard.writeText(getPublicAssetLink(a));
            setSnackbar({ open: true, severity: "success", message: "Public asset link copied." });
          } catch {
            setSnackbar({ open: true, severity: "error", message: "Failed to copy link." });
          }
          setRowMenu({ anchor: null, asset: null });
        }}>
          Copy Public Link
        </MenuItem>
        <MenuItem onClick={() => { 
          setRowMenu({ anchor: null, asset: null }); 
          setSelectedAsset(rowMenu.asset);
          setAssetEditTab(0);
          const a = rowMenu.asset || emptyAsset;
          setAssetForm({
            ...a,
            purchase_date: fmtDate(a.purchase_date),
            warranty_start: fmtDate(a.warranty_start),
            warranty_end: fmtDate(a.warranty_end),
            license_start: fmtDate(a.license_start),
            license_end: fmtDate(a.license_end),
            amc_renewal_date: fmtDate(a.amc_renewal_date),
            cmc_renewal_date: fmtDate(a.cmc_renewal_date),
            allocation_date: fmtDate(a.allocation_date),
            purchase_currency: a.purchase_currency || a.details?.purchase_currency || "INR",
            ...getDepreciationFormValues(a),
          });
          setAssetEditImages([]);
          setAssetEditTab(0);
          setAssetEditOpen(true); 
        }}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => {
          const a = rowMenu.asset;
          if (!a) return;
          setRowMenu({ anchor: null, asset: null });
          setDeleteContext({ mode: 'single', asset: a });
          setDeleteDialogOpen(true);
        }}>
          Delete
        </MenuItem>
        <MenuItem onClick={() => {
          const a = rowMenu.asset;
          setAssetForm(buildCloneAssetForm(a));
          setAssetErrors({});
          setAssetEditTab(0);
          setAssetCreateOpen(true);
          setRowMenu({ anchor: null, asset: null });
        }}>
          Clone
        </MenuItem>
      </Menu>

      <Dialog
        open={assetHistoryOpen}
        onClose={() => setAssetHistoryOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid #E2E8F0",
            boxShadow: "0 20px 45px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
          <HistoryIcon color="primary" />
          Asset & QR Scan History{selectedAsset?.asset_uid ? ` - ${selectedAsset.asset_uid}` : ""}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#FCFDFE" }}>
          {!assetHistoryLoading ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 150px 150px auto" },
                gap: 1.25,
                mb: 2.5,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                label="Search changes"
                placeholder="Change, owner, custodian, department..."
                value={assetHistoryQuery}
                onChange={(event) => setAssetHistoryQuery(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <TextField size="small" label="From" type="date" value={assetHistoryFrom} onChange={(event) => setAssetHistoryFrom(event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" label="To" type="date" value={assetHistoryTo} onChange={(event) => setAssetHistoryTo(event.target.value)} InputLabelProps={{ shrink: true }} />
              <Button
                size="small"
                onClick={() => {
                  setAssetHistoryQuery("");
                  setAssetHistoryFrom("");
                  setAssetHistoryTo("");
                }}
                disabled={!assetHistoryQuery && !assetHistoryFrom && !assetHistoryTo}
              >
                Clear
              </Button>
            </Box>
          ) : null}
          {assetHistoryLoading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">Loading history...</Typography>
            </Stack>
          ) : filteredHistoryTimelineItems.length === 0 ? (
            <Alert severity="info">No history entries match the selected filters.</Alert>
          ) : (
            <Timeline
              position="right"
              sx={{
                m: 0,
                p: 0,
                [`& .MuiTimelineItem-root:before`]: { flex: { xs: 0, sm: 0.18 }, p: 0 },
              }}
            >
              {filteredHistoryTimelineItems.map((item, idx) => {
                const isLast = idx === filteredHistoryTimelineItems.length - 1;
                const dotColor =
                  item.action === "asset_created"
                    ? "success"
                    : item.action === "asset_deleted"
                      ? "error"
                      : item.action === "asset_updated"
                        ? "warning"
                        : "primary";
                return (
                  <TimelineItem key={item.id || idx}>
                    <TimelineOppositeContent
                      sx={{
                        maxWidth: { xs: 0, sm: 220 },
                        display: { xs: "none", sm: "block" },
                        pt: 1.5,
                        pr: 2,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {formatTimelineTime(item.created_at)}
                      </Typography>
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color={dotColor} variant="filled" sx={{ boxShadow: "0 0 0 3px #fff" }} />
                      {!isLast && <TimelineConnector sx={{ bgcolor: "#CBD5E1" }} />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: 1 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          px: 1.5,
                          py: 1.2,
                          borderRadius: 2,
                          borderColor: "#E2E8F0",
                          bgcolor: "#FFFFFF",
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ color: "#0F172A", fontWeight: 700 }}>
                          {item.headline}
                        </Typography>
                        {item.details ? (
                          <Typography variant="body2" sx={{ color: "#475569", mt: 0.35 }}>
                            {item.details}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "block", sm: "none" }, mt: 0.75 }}>
                          {formatTimelineTime(item.created_at)}
                        </Typography>
                      </Paper>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssetHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(15,23,42,0.35)'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <DeleteIcon sx={{ color: '#dc2626' }} />
          <Typography variant="h6" fontWeight={600}>Delete Asset{deleteContext.mode === 'bulk' ? 's' : ''}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {deleteContext.mode === 'bulk'
              ? `Are you sure you want to delete ${selectedIds.length} selected asset(s)? This action cannot be undone.`
              : `Are you sure you want to delete asset ${deleteContext.asset?.asset_uid || deleteContext.asset?.id}? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                if (deleteContext.mode === 'bulk') {
                  if (!selectedIds.length) return;
                  await AssetsAPI.bulkDelete(selectedIds);
                  setSelectedIds([]);
                  setSnackbar({ open: true, severity: 'success', message: 'Selected assets deleted successfully.' });
                } else if (deleteContext.mode === 'single' && deleteContext.asset) {
                  await AssetsAPI.delete(deleteContext.asset.id);
                  setSnackbar({ open: true, severity: 'success', message: 'Asset deleted successfully.' });
                }
                setRefreshKey(k => k + 1);
              } catch (err) {
                console.error('delete asset', err);
                setSnackbar({ open: true, severity: 'error', message: 'Failed to delete asset(s). Please try again.' });
              } finally {
                setDeleteDialogOpen(false);
                setDeleteContext({ mode: null, asset: null });
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Create Dialog */}
      <Dialog 
        open={assetCreateOpen} 
        onClose={() => setAssetCreateOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Add sx={{ color: '#3b82f6' }} />
          <Typography variant="h6" fontWeight={600}>Create New Asset</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Tabs
            value={assetEditTab}
            onChange={(_, value) => setAssetEditTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}
          >
            <Tab label="1. Asset & custody" />
            <Tab label="2. Financial & cover" />
            <Tab label="3. Supporting details" />
          </Tabs>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {assetEditTab === 0 && "Set the organisation owner, the responsible custodian, and the basic asset record."}
            {assetEditTab === 1 && "Record purchase, depreciation, warranty and service-cover information."}
            {assetEditTab === 2 && "Add the invoice link and any notes that help with future audits."}
          </Typography>
          {/* Ownership Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 0 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Ownership Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Asset Owner" 
                  fullWidth 
                  required={!(assetEditOpen && selectedAsset?.record_source === "AGENT")}
                  value={assetForm.owner_name} 
                  onChange={e => setAssetForm(f => ({ ...f, owner_name: e.target.value }))} 
                  error={!!assetErrors.owner_name} 
                  helperText={assetErrors.owner_name || "Company or organization that owns the asset"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Custodian Name" 
                  fullWidth 
                  value={assetForm.asset_custodian || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, asset_custodian: e.target.value }))} 
                  helperText="Employee currently responsible for the asset"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Custodian Email"
                  type="email"
                  fullWidth
                  value={assetForm.asset_custodian_email || ""}
                  onChange={e => setAssetForm(f => ({ ...f, asset_custodian_email: e.target.value }))}
                  error={!!assetErrors.asset_custodian_email}
                  helperText={assetErrors.asset_custodian_email || "Optional email for custody communication"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Custodian Contact Number"
                  fullWidth
                  value={assetForm.asset_custodian_contact || ""}
                  onChange={e => setAssetForm(f => ({ ...f, asset_custodian_contact: e.target.value }))}
                  helperText="Optional phone number"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label={assetEditOpen && selectedAsset?.record_source === "AGENT" ? "Asset Owner Email" : "Asset Owner Email *"}
                  type="email"
                  fullWidth 
                  required={!(assetEditOpen && selectedAsset?.record_source === "AGENT")}
                  value={assetForm.owner_email || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, owner_email: e.target.value }))} 
                  error={!!assetErrors.owner_email}
                  helperText={assetErrors.owner_email || "Audit notifications and ownership communication"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Asset Owner Contact Number"
                  fullWidth 
                  value={assetForm.owner_contact || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, owner_contact: e.target.value }))} 
                  helperText="Phone number"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Department" 
                  fullWidth 
                  required={!(assetEditOpen && selectedAsset?.record_source === "AGENT")}
                  value={assetForm.department} 
                  onChange={e => setAssetForm(f => ({ ...f, department: e.target.value }))} 
                  error={!!assetErrors.department} 
                  helperText={assetErrors.department || "Cost center or department"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Allocation Date" 
                  type="date" 
                  fullWidth 
                  value={assetForm.allocation_date || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, allocation_date: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="When allocated to owner"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Asset Details Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 0 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Asset Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete 
                  freeSolo
                  options={assetTypeOptions} 
                  fullWidth 
                  value={assetForm.asset_type || ""} 
                  getOptionLabel={(option) => humanizeAssetTypeLabel(typeof option === "string" ? option : option?.value)}
                  isOptionEqualToValue={(option, value) => normalizeAssetTypeValue(option) === normalizeAssetTypeValue(value)}
                  onInputChange={(_, v) => setAssetForm(f => ({ ...f, asset_type: normalizeAssetTypeValue(v) }))} 
                  onChange={(_, v) => setAssetForm(f => ({ ...f, asset_type: normalizeAssetTypeValue(v) }))} 
                  renderOption={(props, option) => (
                    <li {...props}>{humanizeAssetTypeLabel(option)}</li>
                  )}
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      size="small" 
                      label="Asset Type *" 
                      required
                      error={!!assetErrors.asset_type}
                      helperText={assetErrors.asset_type || "Select or type a custom type"}
                      sx={{ 
                        bgcolor: 'white',
                        '& .MuiInputLabel-root': { bgcolor: 'white', px: 0.5 },
                        '& .MuiInputBase-input': { color: '#1e293b' }
                      }} 
                    />
                  )} 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ px: 1.5, py: 1.1, minHeight: 58, bgcolor: '#f8fafc', borderColor: '#e2e8f0' }}>
                  <Typography variant="caption" color="text.secondary">Assignment</Typography>
                  <Typography variant="body2" fontWeight={600}>Derived from the linked device and custodian. Lifecycle changes use Retire / Dispose.</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel sx={{ bgcolor: 'white', px: 0.5 }}>Delivery Status *</InputLabel>
                  <Select 
                    label="Delivery Status *" 
                    fullWidth
                    value={assetForm.delivery_status || "ORDERED"} 
                    onChange={e => setAssetForm(f => ({ ...f, delivery_status: e.target.value }))}
                    error={!!assetErrors.delivery_status}
                  >
                    <MenuItem value="ORDERED">Ordered</MenuItem>
                    <MenuItem value="DISPATCHED">Dispatched</MenuItem>
                    <MenuItem value="RECEIVED">Received</MenuItem>
                  </Select>
                  <FormHelperText error={!!assetErrors.delivery_status}>
                    {assetErrors.delivery_status || "Logistics progress from purchase to handover (Ordered, Dispatched, Received)."}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Quantity" 
                  type="number" 
                  fullWidth 
                  value={assetForm.quantity ?? 1} 
                  onChange={e => setAssetForm(f => ({ ...f, quantity: Number(e.target.value || 1) }))} 
                  inputProps={{ min: 1 }} 
                  error={!!assetErrors.quantity}
                  helperText={assetErrors.quantity || "Units acquired"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              
              {["RAM", "SSD", "HDD", "GPU"].includes(assetForm.asset_type) && (
                <Grid item xs={12} sm={6}>
                  <TextField 
                    size="small" 
                    label="Capacity" 
                    fullWidth 
                    placeholder="e.g., 16 GB, 512 GB"
                    value={assetForm.details?.capacity || ""} 
                    onChange={e => setAssetForm(f => ({ ...f, details: { ...(f.details || {}), capacity: e.target.value } }))}
                    sx={{ bgcolor: 'white' }}
                  />
                </Grid>
              )}

              {["DEVICE", "LAPTOP", "DESKTOP", "SERVER", "WORKSTATION"].includes(assetForm.asset_type) && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    label="Serial number"
                    fullWidth
                    inputProps={{ maxLength: 255 }}
                    value={assetForm.details?.serial_number || ""}
                    onChange={e => setAssetForm(f => ({ ...f, details: { ...(f.details || {}), serial_number: e.target.value } }))}
                    helperText="Used to match this asset with a future agent report"
                    sx={{ bgcolor: 'white' }}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Autocomplete 
                  options={brandOptions} 
                  freeSolo 
                  fullWidth 
                  value={assetForm.vendor_name || ""} 
                  onInputChange={(_, v) => setAssetForm(f => ({ ...f, vendor_name: v || "" }))}
                  onChange={(_, v) => setAssetForm(f => ({ ...f, vendor_name: v || "" }))} 
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      size="small" 
                      label="Brand / Vendor" 
                      helperText="Select or type custom"
                      sx={{ 
                        bgcolor: 'white',
                        '& .MuiInputLabel-root': { bgcolor: 'white', px: 0.5 },
                        '& .MuiInputBase-input': { color: '#1e293b' }
                      }} 
                    />
                  )} 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Location" 
                  fullWidth 
                  value={assetForm.location || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, location: e.target.value }))} 
                  helperText="Physical location"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Financial Details Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 1 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Financial Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Purchase Price" 
                  type="number" 
                  fullWidth 
                  required={!(assetEditOpen && selectedAsset?.record_source === "AGENT")}
                  value={assetForm.purchase_price} 
                  onChange={e => setAssetForm(f => ({ ...f, purchase_price: e.target.value }))} 
                  error={!!assetErrors.purchase_price} 
                  helperText={assetErrors.purchase_price || `Amount in ${assetForm.purchase_currency || "INR"}`} 
                  InputProps={{ startAdornment: <InputAdornment position="start">{assetForm.purchase_currency || "INR"}</InputAdornment> }} 
                  inputProps={{ step: "0.01", min: "0" }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={assetForm.purchase_currency || "INR"}
                    label="Currency"
                    onChange={e => setAssetForm(f => ({ ...f, purchase_currency: e.target.value }))}
                  >
                    {PURCHASE_CURRENCY_OPTIONS.map(cur => (
                      <MenuItem key={cur} value={cur}>{cur}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Purchase Date" 
                  type="date" 
                  fullWidth 
                  required
                  value={assetForm.purchase_date || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, purchase_date: e.target.value }))} 
                  InputLabelProps={{ shrink: true }} 
                  error={!!assetErrors.purchase_date} 
                  helperText={assetErrors.purchase_date || "Acquisition date"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel>Depreciation Method</InputLabel>
                  <Select
                    value={assetForm.depreciation_method || "SLM"}
                    label="Depreciation Method"
                    onChange={e => setAssetForm(f => ({ ...f, depreciation_method: e.target.value }))}
                    error={!!assetErrors.depreciation_method}
                  >
                    {DEPRECIATION_METHOD_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!assetErrors.depreciation_method}>
                    {assetErrors.depreciation_method || "Choose Straight-Line or Written Down Value."}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Useful Life (Months)"
                  type="number"
                  fullWidth
                  value={assetForm.useful_life_months}
                  onChange={e => setAssetForm(f => ({ ...f, useful_life_months: e.target.value }))}
                  error={!!assetErrors.useful_life_months}
                  helperText={assetErrors.useful_life_months || "Optional if rate is used. Example: 48 months = 4 years."}
                  inputProps={{ min: "0", step: "1" }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Depreciation Rate (%)"
                  type="number"
                  fullWidth
                  value={assetForm.depreciation_rate_percent}
                  onChange={e => setAssetForm(f => ({ ...f, depreciation_rate_percent: e.target.value }))}
                  error={!!assetErrors.depreciation_rate_percent}
                  helperText={assetErrors.depreciation_rate_percent || "Optional for WDV. Annual percentage rate."}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  inputProps={{ min: "0", step: "0.1" }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Salvage Value"
                  type="number"
                  fullWidth
                  value={assetForm.salvage_value}
                  onChange={e => setAssetForm(f => ({ ...f, salvage_value: e.target.value }))}
                  error={!!assetErrors.salvage_value}
                  helperText={assetErrors.salvage_value || "Floor limit. NBV will not drop below this amount."}
                  InputProps={{ startAdornment: <InputAdornment position="start">{assetForm.purchase_currency || "INR"}</InputAdornment> }}
                  inputProps={{ min: "0", step: "0.01" }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel>Depreciation Start</InputLabel>
                  <Select
                    value={assetForm.depreciation_start_mode || "PURCHASE_DATE"}
                    label="Depreciation Start"
                    onChange={e => setAssetForm(f => ({ ...f, depreciation_start_mode: e.target.value }))}
                    error={!!assetErrors.depreciation_start_mode}
                  >
                    {DEPRECIATION_START_MODE_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!assetErrors.depreciation_start_mode}>
                    {assetErrors.depreciation_start_mode || "Use this to control mid-month onboarding."}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Warranty & License Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 1 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Warranty and License Period
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Warranty Start" 
                  type="date" 
                  fullWidth 
                  value={assetForm.warranty_start || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, warranty_start: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Warranty End" 
                  type="date" 
                  fullWidth 
                  value={assetForm.warranty_end || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, warranty_end: e.target.value }))} 
                  InputLabelProps={{ shrink: true }} 
                  helperText="Alerts when ≤30 days"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="License Start" 
                  type="date" 
                  fullWidth 
                  value={assetForm.license_start || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, license_start: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="For software licenses"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="License End" 
                  type="date" 
                  fullWidth 
                  value={assetForm.license_end || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, license_end: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="License expiry date"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* AMC/CMC Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 1 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              AMC / CMC Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white', minWidth: { sm: 260 } }}>
                  <InputLabel sx={{ bgcolor: 'white', px: 0.5 }}>AMC Status</InputLabel>
                  <Select 
                    label="AMC Status" 
                    fullWidth
                    value={assetForm.amc_status || "None"} 
                    onChange={e => setAssetForm(f => ({ ...f, amc_status: e.target.value }))}
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="ACTIVE">✓ Active</MenuItem>
                    <MenuItem value="EXPIRED">✕ Expired</MenuItem>
                    <MenuItem value="PENDING">⏳ Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField 
                  size="small" 
                  label="AMC Renewal Date" 
                  type="date" 
                  fullWidth 
                  value={assetForm.amc_renewal_date || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, amc_renewal_date: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="Next renewal date"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white', minWidth: { sm: 260 } }}>
                  <InputLabel sx={{ bgcolor: 'white', px: 0.5 }}>CMC Status</InputLabel>
                  <Select 
                    label="CMC Status" 
                    fullWidth
                    value={assetForm.cmc_status || "None"} 
                    onChange={e => setAssetForm(f => ({ ...f, cmc_status: e.target.value }))}
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="ACTIVE">✓ Active</MenuItem>
                    <MenuItem value="EXPIRED">✕ Expired</MenuItem>
                    <MenuItem value="PENDING">⏳ Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField 
                  size="small" 
                  label="CMC Renewal Date" 
                  type="date" 
                  fullWidth 
                  value={assetForm.cmc_renewal_date || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, cmc_renewal_date: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="Next renewal date"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Additional Notes */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 2 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Additional Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField 
                  size="small" 
                  label="Invoice URL" 
                  fullWidth 
                  value={assetForm.invoice_url || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, invoice_url: e.target.value }))} 
                  helperText="Link to invoice document"
                  sx={{ bgcolor: 'white' }}
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  size="small" 
                  label="Remarks / Notes" 
                  fullWidth 
                  multiline 
                  rows={3} 
                  value={assetForm.remarks || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, remarks: e.target.value }))} 
                  helperText={`${(assetForm.remarks || "").length}/250 characters`}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Box sx={{ width: '100%' }}>
                <AssetCustomFieldInputs
                  fields={assetCustomFields}
                  values={assetForm.details?.custom_fields || {}}
                  onChange={(fieldKey, value) => setAssetForm((form) => ({ ...form, details: { ...(form.details || {}), custom_fields: { ...(form.details?.custom_fields || {}), [fieldKey]: value } } }))}
                />
              </Box>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button onClick={() => setAssetCreateOpen(false)} sx={{ color: '#64748b' }}>
            Cancel
          </Button>
          {assetEditTab > 0 && (
            <Button onClick={() => setAssetEditTab((tab) => tab - 1)} sx={{ color: '#475569' }}>
              Back
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {assetEditTab < 2 ? (
            <Button variant="contained" onClick={() => setAssetEditTab((tab) => tab + 1)} sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              Continue
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => submitCreate(true)}
                sx={{ borderColor: '#cbd5e1', color: '#475569' }}
              >
                Save & Add Another
              </Button>
              <LoadingButton
                loading={assetSaving}
                variant="contained"
                onClick={() => submitCreate(false)}
                sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
              >
                Create Asset
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={assetEditOpen} 
        onClose={() => {
          setAssetEditOpen(false);
          setAssetEditImages([]);
        }} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Typography variant="h6" fontWeight={600}>Edit Asset</Typography>
          {selectedAsset && (
            <Chip 
              label={selectedAsset.asset_uid || selectedAsset.id} 
              size="small" 
              sx={{ bgcolor: '#f1f5f9', fontFamily: 'monospace' }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedAsset?.record_source === "AGENT" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This asset was found by an agent. Add ownership and purchase details when available; you can save partial information.
            </Alert>
          )}
          <Tabs
            value={assetEditTab}
            onChange={(_, value) => setAssetEditTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2, borderBottom: '1px solid #e2e8f0' }}
          >
            <Tab label="1. Asset & custody" />
            <Tab label="2. Financial & cover" />
            <Tab label="3. Supporting details" />
          </Tabs>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {assetEditTab === 0 && "Ownership is held by the organisation; the custodian is the person accountable for the asset."}
            {assetEditTab === 1 && "Keep financial, warranty and service-cover details together for renewal and audit reporting."}
            {assetEditTab === 2 && "Maintain photos, invoice details, history and supporting notes in one place."}
          </Typography>
          {/* Ownership Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 0 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Ownership Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Asset Owner" 
                  fullWidth 
                  required={selectedAsset?.record_source !== "AGENT"}
                  value={assetForm.owner_name} 
                  onChange={e => setAssetForm(f => ({ ...f, owner_name: e.target.value }))} 
                  error={!!assetErrors.owner_name} 
                  helperText={assetErrors.owner_name || "Company or organization that owns the asset"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Custodian Name" 
                  fullWidth 
                  value={assetForm.asset_custodian || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, asset_custodian: e.target.value }))} 
                  helperText="Employee currently responsible for the asset"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Custodian Email"
                  type="email"
                  fullWidth
                  value={assetForm.asset_custodian_email || ""}
                  onChange={e => setAssetForm(f => ({ ...f, asset_custodian_email: e.target.value }))}
                  error={!!assetErrors.asset_custodian_email}
                  helperText={assetErrors.asset_custodian_email || "Optional email for custody communication"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Custodian Contact Number"
                  fullWidth
                  value={assetForm.asset_custodian_contact || ""}
                  onChange={e => setAssetForm(f => ({ ...f, asset_custodian_contact: e.target.value }))}
                  helperText="Optional phone number"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label={selectedAsset?.record_source === "AGENT" ? "Asset Owner Email" : "Asset Owner Email *"}
                  type="email"
                  fullWidth 
                  required={selectedAsset?.record_source !== "AGENT"}
                  value={assetForm.owner_email || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, owner_email: e.target.value }))} 
                  error={!!assetErrors.owner_email}
                  helperText={assetErrors.owner_email || "Audit notifications and ownership communication"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Asset Owner Contact"
                  fullWidth 
                  value={assetForm.owner_contact || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, owner_contact: e.target.value }))} 
                  helperText="Phone number"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Department" 
                  fullWidth 
                  required={selectedAsset?.record_source !== "AGENT"}
                  value={assetForm.department} 
                  onChange={e => setAssetForm(f => ({ ...f, department: e.target.value }))} 
                  error={!!assetErrors.department} 
                  helperText={assetErrors.department || "Cost center or department"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Allocation Date" 
                  type="date" 
                  fullWidth 
                  value={assetForm.allocation_date || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, allocation_date: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="Date assigned to owner"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Asset Details Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 0 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Asset Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete 
                  freeSolo
                  options={assetTypeOptions} 
                  fullWidth 
                  value={assetForm.asset_type || ""} 
                  getOptionLabel={(option) => humanizeAssetTypeLabel(typeof option === "string" ? option : option?.value)}
                  isOptionEqualToValue={(option, value) => normalizeAssetTypeValue(option) === normalizeAssetTypeValue(value)}
                  onInputChange={(_, v) => setAssetForm(f => ({ ...f, asset_type: normalizeAssetTypeValue(v) }))} 
                  onChange={(_, v) => setAssetForm(f => ({ ...f, asset_type: normalizeAssetTypeValue(v) }))} 
                  renderOption={(props, option) => (
                    <li {...props}>{humanizeAssetTypeLabel(option)}</li>
                  )}
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      size="small" 
                      label="Asset Type *" 
                      required
                      error={!!assetErrors.asset_type}
                      helperText={assetErrors.asset_type || "Select or type a custom type"}
                      sx={{ 
                        bgcolor: 'white',
                        '& .MuiInputLabel-root': {
                          bgcolor: 'white',
                          px: 0.5
                        },
                        '& .MuiInputBase-input': {
                          color: '#1e293b'
                        }
                      }} 
                    />
                  )} 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ px: 1.5, py: 1.1, minHeight: 58, bgcolor: '#f8fafc', borderColor: '#e2e8f0' }}>
                  <Typography variant="caption" color="text.secondary">Assignment</Typography>
                  <Typography variant="body2" fontWeight={600}>Derived from the linked device and custodian. Lifecycle changes use Retire / Dispose.</Typography>
                </Paper>
              </Grid>
              {["DEVICE", "LAPTOP", "DESKTOP", "SERVER", "WORKSTATION"].includes(assetForm.asset_type) && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    label="Serial number"
                    fullWidth
                    inputProps={{ maxLength: 255 }}
                    value={assetForm.details?.serial_number || ""}
                    onChange={e => setAssetForm(f => ({ ...f, details: { ...(f.details || {}), serial_number: e.target.value } }))}
                    helperText="Used to match this asset with agent reports"
                    sx={{ bgcolor: 'white' }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel sx={{ bgcolor: 'white', px: 0.5 }}>Delivery Status</InputLabel>
                  <Select 
                    label="Delivery Status"
                    fullWidth
                    value={assetForm.delivery_status ?? ""}
                    onChange={e => setAssetForm(f => ({ ...f, delivery_status: e.target.value || null }))}
                    error={!!assetErrors.delivery_status}
                  >
                    <MenuItem value="">Not recorded</MenuItem>
                    <MenuItem value="ORDERED">Ordered</MenuItem>
                    <MenuItem value="DISPATCHED">Dispatched</MenuItem>
                    <MenuItem value="RECEIVED">Received</MenuItem>
                  </Select>
                  <FormHelperText error={!!assetErrors.delivery_status}>
                    {assetErrors.delivery_status || "Logistics progress from purchase to handover (Ordered, Dispatched, Received)."}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Quantity" 
                  type="number" 
                  fullWidth 
                  value={assetForm.quantity ?? 1} 
                  onChange={e => setAssetForm(f => ({ ...f, quantity: Number(e.target.value || 1) }))} 
                  inputProps={{ min: 1 }} 
                  error={!!assetErrors.quantity}
                  helperText={assetErrors.quantity || "Units acquired"}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete 
                  options={brandOptions} 
                  freeSolo 
                  fullWidth 
                  value={assetForm.vendor_name || ""} 
                  onInputChange={(_, v) => setAssetForm(f => ({ ...f, vendor_name: v || "" }))} 
                  onChange={(_, v) => setAssetForm(f => ({ ...f, vendor_name: v || "" }))} 
                  renderInput={params => (
                    <TextField 
                      {...params} 
                      size="small" 
                      label="Brand / Vendor" 
                      helperText="Select or enter custom brand"
                      sx={{ 
                        bgcolor: 'white',
                        '& .MuiInputLabel-root': {
                          bgcolor: 'white',
                          px: 0.5
                        },
                        '& .MuiInputBase-input': {
                          color: '#1e293b'
                        }
                      }} 
                    />
                  )} 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Location" 
                  fullWidth 
                  value={assetForm.location || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, location: e.target.value }))} 
                  helperText="Physical location"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 2 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Asset Photos
            </Typography>
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Existing photos can be removed any time. Uploading additional images stays available only on paid plans.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assetImageCount} / {MAX_ASSET_IMAGE_COUNT} photos already attached
              </Typography>
              {assetImageCount ? (
                <Stack direction="row" flexWrap="wrap" gap={1.25}>
                  {(selectedAsset?.details?.asset_images || []).slice(0, MAX_ASSET_IMAGE_COUNT).map((item, index) => {
                    const mediaUrl = typeof item === "string" ? item : item?.url;
                    if (!mediaUrl) return null;
                    const removingKey = String(item?.blob_name || item?.blobName || mediaUrl);
                    return (
                      <Box
                        key={`${removingKey}-${index}`}
                        sx={{
                          position: "relative",
                          width: 92,
                          height: 92,
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          overflow: "hidden",
                          bgcolor: "#fff",
                          flex: "0 0 auto",
                        }}
                      >
                        <Box
                          component="img"
                          src={mediaUrl}
                          alt={`Asset ${index + 1}`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Tooltip title="Remove photo">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => removeAssetImage(item)}
                              disabled={assetImageRemoving === removingKey}
                              sx={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                bgcolor: "rgba(15,23,42,0.72)",
                                color: "#fff",
                                "&:hover": { bgcolor: "rgba(15,23,42,0.88)" },
                              }}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No picture added.
                </Typography>
              )}
              {assetMediaLocked ? (
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Upgrade to upload up to 3 asset images for richer records and faster verification.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(ROUTES.PRICING)}
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    Upgrade to enable photos
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1.25}>
                  <Typography variant="body2" color="text.secondary">
                    Upload up to 3 images, each smaller than 2 MB. These help admins confirm physical asset condition and location.
                  </Typography>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    sx={{ alignSelf: "flex-start", textTransform: "none" }}
                    disabled={assetImageCount >= MAX_ASSET_IMAGE_COUNT}
                  >
                    {assetImageCount >= MAX_ASSET_IMAGE_COUNT ? "Photo limit reached" : "Add images"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const tooLarge = files.find((file) => file.size > MAX_ASSET_IMAGE_SIZE_BYTES);
                        if (tooLarge) {
                          setAssetErrors((prev) => ({ ...prev, asset_images: "Each image must be smaller than 2 MB." }));
                          e.target.value = "";
                          return;
                        }
                        assetEditImagePreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
                        const uploadLimit = Math.max(0, remainingAssetImageSlots);
                        const selectedFiles = files.slice(0, Math.min(MAX_ASSET_IMAGE_COUNT, uploadLimit));
                        const previews = selectedFiles.map((file) => ({
                          name: file.name,
                          url: URL.createObjectURL(file),
                        }));
                        if (files.length > selectedFiles.length) {
                          setAssetErrors((prev) => ({ ...prev, asset_images: `You can add only ${selectedFiles.length} more image${selectedFiles.length === 1 ? "" : "s"}.` }));
                        } else {
                          setAssetErrors((prev) => ({ ...prev, asset_images: "" }));
                        }
                        setAssetEditImages(selectedFiles);
                        setAssetEditImagePreviews(previews);
                      }}
                    />
                  </Button>
                  {assetEditImages.length ? (
                    <Typography variant="body2" color="text.secondary">
                      {assetEditImages.length} photo{assetEditImages.length > 1 ? "s" : ""} selected
                    </Typography>
                  ) : null}
                  {assetEditImagePreviews.length ? (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {assetEditImagePreviews.map((preview, index) => (
                        <Box
                          key={`${preview.url}-${index}`}
                          sx={{
                            width: 84,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            border: "1px solid #cbd5e1",
                            bgcolor: "#fff",
                          }}
                        >
                          <Box
                            component="img"
                            src={preview.url}
                            alt={preview.name || `Preview ${index + 1}`}
                            sx={{ width: "100%", height: 84, objectFit: "cover" }}
                          />
                          <Typography variant="caption" sx={{ display: "block", px: 0.75, py: 0.4, color: "text.secondary" }}>
                            Preview
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
                  {assetErrors.asset_images ? (
                    <Typography variant="body2" color="error.main">
                      {assetErrors.asset_images}
                    </Typography>
                  ) : null}
                </Stack>
              )}
            </Stack>
          </Paper>

          {/* Financial Details Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 1 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Financial Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Purchase Price" 
                  type="number" 
                  fullWidth 
                  required={selectedAsset?.record_source !== "AGENT"}
                  value={assetForm.purchase_price} 
                  onChange={e => setAssetForm(f => ({ ...f, purchase_price: e.target.value }))} 
                  error={!!assetErrors.purchase_price} 
                  helperText={assetErrors.purchase_price || `Amount in ${assetForm.purchase_currency || "INR"}`} 
                  InputProps={{ startAdornment: <InputAdornment position="start">{assetForm.purchase_currency || "INR"}</InputAdornment> }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={assetForm.purchase_currency || "INR"}
                    label="Currency"
                    onChange={e => setAssetForm(f => ({ ...f, purchase_currency: e.target.value }))}
                  >
                    {PURCHASE_CURRENCY_OPTIONS.map(cur => (
                      <MenuItem key={cur} value={cur}>{cur}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Purchase Date" 
                  type="date" 
                  fullWidth 
                  value={assetForm.purchase_date || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, purchase_date: e.target.value }))} 
                  InputLabelProps={{ shrink: true }} 
                  helperText="Acquisition date"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel>Depreciation Method</InputLabel>
                  <Select
                    value={assetForm.depreciation_method || "SLM"}
                    label="Depreciation Method"
                    onChange={e => setAssetForm(f => ({ ...f, depreciation_method: e.target.value }))}
                    error={!!assetErrors.depreciation_method}
                  >
                    {DEPRECIATION_METHOD_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!assetErrors.depreciation_method}>
                    {assetErrors.depreciation_method || "Choose Straight-Line or Written Down Value."}
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small"
                  label="Useful Life (Months)"
                  type="number"
                  fullWidth
                  value={assetForm.useful_life_months}
                  onChange={e => setAssetForm(f => ({ ...f, useful_life_months: e.target.value }))}
                  error={!!assetErrors.useful_life_months}
                  helperText={assetErrors.useful_life_months || "Optional if rate is used. Example: 48 months = 4 years."}
                  inputProps={{ min: "0", step: "1" }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small"
                  label="Depreciation Rate (%)"
                  type="number"
                  fullWidth
                  value={assetForm.depreciation_rate_percent}
                  onChange={e => setAssetForm(f => ({ ...f, depreciation_rate_percent: e.target.value }))}
                  error={!!assetErrors.depreciation_rate_percent}
                  helperText={assetErrors.depreciation_rate_percent || "Optional for WDV. Annual percentage rate."}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  inputProps={{ min: "0", step: "0.1" }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small"
                  label="Salvage Value"
                  type="number"
                  fullWidth
                  value={assetForm.salvage_value}
                  onChange={e => setAssetForm(f => ({ ...f, salvage_value: e.target.value }))}
                  error={!!assetErrors.salvage_value}
                  helperText={assetErrors.salvage_value || "Floor limit. NBV will not drop below this amount."}
                  InputProps={{ startAdornment: <InputAdornment position="start">{assetForm.purchase_currency || "INR"}</InputAdornment> }}
                  inputProps={{ min: "0", step: "0.01" }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                  <InputLabel>Depreciation Start</InputLabel>
                  <Select
                    value={assetForm.depreciation_start_mode || "PURCHASE_DATE"}
                    label="Depreciation Start"
                    onChange={e => setAssetForm(f => ({ ...f, depreciation_start_mode: e.target.value }))}
                    error={!!assetErrors.depreciation_start_mode}
                  >
                    {DEPRECIATION_START_MODE_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!assetErrors.depreciation_start_mode}>
                    {assetErrors.depreciation_start_mode || "Use this to control mid-month onboarding."}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Warranty & License Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 1 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Warranty and License Period
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Warranty Start" 
                  type="date" 
                  fullWidth 
                  value={assetForm.warranty_start || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, warranty_start: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="Warranty End" 
                  type="date" 
                  fullWidth 
                  value={assetForm.warranty_end || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, warranty_end: e.target.value }))} 
                  InputLabelProps={{ shrink: true }} 
                  helperText="Alerts when ≤30 days"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="License Start" 
                  type="date" 
                  fullWidth 
                  value={assetForm.license_start || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, license_start: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="Software/subscription start"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  size="small" 
                  label="License End" 
                  type="date" 
                  fullWidth 
                  value={assetForm.license_end || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, license_end: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="Expiration date"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* AMC / CMC Status Section */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 1 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              AMC / CMC Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white', minWidth: { sm: 260 } }}>
                  <InputLabel sx={{ bgcolor: 'white', px: 0.5 }}>AMC Status</InputLabel>
                  <Select 
                    label="AMC Status" 
                    fullWidth
                    value={assetForm.amc_status || "None"} 
                    onChange={e => setAssetForm(f => ({ ...f, amc_status: e.target.value }))}
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="ACTIVE">✓ Active</MenuItem>
                    <MenuItem value="EXPIRED">✕ Expired</MenuItem>
                    <MenuItem value="PENDING">⏳ Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField 
                  size="small" 
                  label="AMC Renewal Date" 
                  type="date" 
                  fullWidth 
                  value={assetForm.amc_renewal_date || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, amc_renewal_date: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="Next renewal date"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth size="small" sx={{ bgcolor: 'white', minWidth: { sm: 260 } }}>
                  <InputLabel sx={{ bgcolor: 'white', px: 0.5 }}>CMC Status</InputLabel>
                  <Select 
                    label="CMC Status" 
                    fullWidth
                    value={assetForm.cmc_status || "None"} 
                    onChange={e => setAssetForm(f => ({ ...f, cmc_status: e.target.value }))}
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="ACTIVE">✓ Active</MenuItem>
                    <MenuItem value="EXPIRED">✕ Expired</MenuItem>
                    <MenuItem value="PENDING">⏳ Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField 
                  size="small" 
                  label="CMC Renewal Date" 
                  type="date" 
                  fullWidth 
                  value={assetForm.cmc_renewal_date || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, cmc_renewal_date: e.target.value }))} 
                  InputLabelProps={{ shrink: true }}
                  helperText="Next renewal date"
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
            </Grid>
          </Paper>

          {assetEditTab === 2 && (
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2}>
                Asset History
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Last Scanned</Typography>
                  <Typography variant="body2" fontWeight={600}>{fmtDateTime(selectedAsset?.last_scanned_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Linked Device</Typography>
                  <Typography variant="body2" fontWeight={600}>{selectedAsset?.Device?.hostname || 'Unassigned'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Asset UID</Typography>
                  <Typography variant="body2" fontWeight={600}>{selectedAsset?.asset_uid || selectedAsset?.id || '—'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Additional Notes */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, display: assetEditTab === 2 ? 'block' : 'none' }}>
            <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Additional Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField 
                  size="small" 
                  label="Invoice URL" 
                  fullWidth 
                  value={assetForm.invoice_url || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, invoice_url: e.target.value }))} 
                  helperText="Link to invoice document"
                  sx={{ bgcolor: 'white' }}
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  size="small" 
                  label="Remarks / Notes" 
                  fullWidth 
                  multiline 
                  rows={3} 
                  value={assetForm.remarks || ""} 
                  onChange={e => setAssetForm(f => ({ ...f, remarks: e.target.value }))} 
                  helperText={`${(assetForm.remarks || "").length}/250 characters`}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Box sx={{ width: '100%' }}>
                <AssetCustomFieldInputs
                  fields={assetCustomFields}
                  values={assetForm.details?.custom_fields || {}}
                  onChange={(fieldKey, value) => setAssetForm((form) => ({ ...form, details: { ...(form.details || {}), custom_fields: { ...(form.details?.custom_fields || {}), [fieldKey]: value } } }))}
                />
              </Box>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button onClick={() => { setAssetEditOpen(false); setAssetEditImages([]); setAssetEditImagePreviews([]); }} sx={{ color: '#64748b' }}>
            Cancel
          </Button>
          {assetEditTab > 0 && (
            <Button onClick={() => setAssetEditTab((tab) => tab - 1)} sx={{ color: '#475569' }}>
              Back
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          {assetEditTab < 2 ? (
            <Button variant="contained" onClick={() => setAssetEditTab((tab) => tab + 1)} sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}>
              Continue
            </Button>
          ) : (
            <LoadingButton
              loading={assetSaving}
              variant="contained"
              onClick={submitEdit}
              sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
            >
              Save Changes
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog 
        open={assetViewOpen} 
        onClose={() => {
          setAssetViewOpen(false);
          setAssetImageReplaceTarget(null);
          if (assetImageReplaceInputRef.current) assetImageReplaceInputRef.current.value = "";
        }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(15,23,42,0.2)',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1.5, 
          borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)',
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Asset Details</Typography>
              <Typography variant="caption" color="text.secondary">Comprehensive snapshot of ownership, lifecycle, and warranty.</Typography>
            </Box>
            {selectedAsset ? (
              <Chip
                label={selectedAsset.asset_uid || selectedAsset.id}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace', borderColor: '#cbd5e1' }}
              />
            ) : null}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.2, pb: 2.5, bgcolor: '#FCFDFE' }}>
          {selectedAsset ? (
            <Stack spacing={2}>
              {/* Header Card */}
              <Paper variant="outlined" sx={{ p: 2.2, bgcolor: '#F8FBFF', border: '1px solid #DCEAF8', borderRadius: 2.2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: `${getAssetTypeMeta(selectedAsset.asset_type).color}20`,
                          color: getAssetTypeMeta(selectedAsset.asset_type).color,
                        }}
                      >
                        {getAssetTypeMeta(selectedAsset.asset_type).icon}
                      </Avatar>
                      <Typography variant="h6" fontWeight={600} color="#1e293b">
                        {selectedAsset.vendor_name || selectedAsset.asset_type || 'Asset'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                      <Chip 
                        label={getAssetTypeMeta(selectedAsset.asset_type).label} 
                        size="small" 
                        sx={{ bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 500 }}
                      />
                      <Chip label={assetSourceLabel(selectedAsset.record_source)} size="small" variant="outlined" />
                      {selectedAsset.last_verified_source === "AGENT" && (
                        <Chip label="Verified by agent" size="small" color="success" variant="outlined" />
                      )}
                      <Chip 
                        label={getAssetLifecycleLabel(selectedAsset)}
                        size="small" 
                        sx={{ 
                          bgcolor: getAssetLifecycleLabel(selectedAsset) === 'Assigned'
                            ? '#dcfce7'
                            : ['RETIRED', 'DISPOSED', 'SCRAP'].includes(String(selectedAsset.asset_status || '').toUpperCase())
                            ? '#fee2e2'
                            : '#f1f5f9',
                          color: getAssetLifecycleLabel(selectedAsset) === 'Assigned'
                            ? '#166534'
                            : ['RETIRED', 'DISPOSED', 'SCRAP'].includes(String(selectedAsset.asset_status || '').toUpperCase())
                            ? '#991b1b'
                            : '#475569',
                          fontWeight: 500 
                        }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} mt={1.2} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={() => window.open(getPublicAssetLink(selectedAsset), '_blank', 'noopener,noreferrer')}
                      >
                        Open Public Details
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(getPublicAssetLink(selectedAsset));
                            setSnackbar({ open: true, severity: 'success', message: 'Public asset link copied.' });
                          } catch {
                            setSnackbar({ open: true, severity: 'error', message: 'Failed to copy link.' });
                          }
                        }}
                      >
                        Copy Public Link
                      </Button>
                      {!['RETIRED', 'DISPOSED', 'SCRAP'].includes(String(selectedAsset.asset_status || '').toUpperCase()) ? (
                        <Button size="small" variant="outlined" color="warning" startIcon={<DeleteSweepIcon />} onClick={() => openDisposal('RETIRE')}>
                          Retire asset
                        </Button>
                      ) : null}
                      {String(selectedAsset.asset_status || '').toUpperCase() === 'RETIRED' ? (
                        <Button size="small" variant="contained" color="error" startIcon={<DeleteSweepIcon />} onClick={() => openDisposal('DISPOSE')}>
                          Finalize disposal
                        </Button>
                      ) : null}
                      {String(selectedAsset.asset_status || '').toUpperCase() === 'DISPOSED' ? (
                        <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={downloadDisposalCertificate}>
                          Disposal certificate
                        </Button>
                      ) : null}
                    </Stack>
                  </Box>
                  <Stack alignItems="flex-end" spacing={1}>
                    <Box
                      sx={{
                        width: 96,
                        height: 96,
                        border: "1px solid #E2E8F0",
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#FFFFFF",
                        overflow: "hidden",
                      }}
                    >
                      {getSafeQrImageUrl(selectedAsset) ? (
                        <img
                          src={getSafeQrImageUrl(selectedAsset)}
                          alt={`QR ${selectedAsset.asset_uid || selectedAsset.id}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <QrCode2Icon sx={{ color: "#94A3B8", fontSize: 30 }} />
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon fontSize="small" />}
                      onClick={() => downloadBrandedQrImage(selectedAsset)}
                    >
                      Print Label
                    </Button>
                  </Stack>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2}>
                  Asset Photos
                </Typography>
                {Array.isArray(selectedAsset?.details?.asset_images) && selectedAsset.details.asset_images.length ? (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {selectedAsset.details.asset_images.slice(0, MAX_ASSET_IMAGE_COUNT).map((item, index) => {
                      const mediaUrl = typeof item === "string" ? item : item?.url;
                      if (!mediaUrl) return null;
                      const removingKey = String(item?.blob_name || item?.blobName || mediaUrl);
                      return (
                        <Box
                          key={`${mediaUrl}-${index}`}
                          sx={{
                            position: "relative",
                            width: 92,
                            height: 92,
                            borderRadius: 1.5,
                            border: "1px solid #e2e8f0",
                            overflow: "hidden",
                            bgcolor: "#fff",
                          }}
                        >
                          <Box
                            component="img"
                            src={mediaUrl}
                            alt={`Asset ${index + 1}`}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              display: "flex",
                              gap: 0.5,
                            }}
                          >
                            <Tooltip title="Replace photo">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => startReplaceAssetImage(item)}
                                  disabled={assetImageReplacing === removingKey || assetImageRemoving === removingKey}
                                  sx={{
                                    width: 26,
                                    height: 26,
                                    bgcolor: "rgba(15,23,42,0.76)",
                                    color: "#fff",
                                    "&:hover": { bgcolor: "rgba(15,23,42,0.9)" },
                                  }}
                                >
                                  <SwapHorizIcon fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove photo">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => removeAssetImage(item)}
                                  disabled={assetImageRemoving === removingKey || assetImageReplacing === removingKey}
                                  sx={{
                                    width: 26,
                                    height: 26,
                                    bgcolor: "rgba(15,23,42,0.76)",
                                    color: "#fff",
                                    "&:hover": { bgcolor: "rgba(15,23,42,0.9)" },
                                  }}
                                >
                                  <DeleteIcon fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No picture added.
                  </Typography>
                )}
              </Paper>
              <input
                ref={assetImageReplaceInputRef}
                hidden
                type="file"
                accept="image/*"
                onChange={handleAssetImageReplaceChange}
              />

              {/* Ownership Info */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2}>
                  Ownership Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Owner
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedAsset.owner_name || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Custodian Name
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedAsset.asset_custodian || 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Custodian Email
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
                      {selectedAsset.asset_custodian_email || 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Custodian Contact
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedAsset.asset_custodian_contact || 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Department
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedAsset.department || '—'}
                    </Typography>
                  </Grid>
                  {selectedAsset.owner_email && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                        Email
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
                        {selectedAsset.owner_email}
                      </Typography>
                    </Grid>
                  )}
                  {selectedAsset.owner_contact && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                        Contact
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedAsset.owner_contact}
                      </Typography>
                    </Grid>
                  )}
                  {selectedAsset.allocation_date && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                        Allocation Date
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {toDMY(selectedAsset.allocation_date)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SwapHorizIcon fontSize="small" />}
                        onClick={openCustodyTransferDialog}
                      >
                        Transfer Custody
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Asset Details */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2}>
                  Asset Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Delivery Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={getDeliveryStatusMeta(selectedAsset.delivery_status).label}
                        size="small"
                        sx={{
                          bgcolor: getDeliveryStatusMeta(selectedAsset.delivery_status).bg,
                          color: getDeliveryStatusMeta(selectedAsset.delivery_status).fg,
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Quantity
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedAsset.quantity ?? 1}
                    </Typography>
                  </Grid>
                  {selectedAsset.location && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                        Location
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedAsset.location}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Financial Info */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2}>
                  Financial Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Purchase Price
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="#10b981">
                      {formatPurchaseAmount(selectedAsset)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                      Purchase Date
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {toDMY(selectedAsset.purchase_date)}
                    </Typography>
                  </Grid>
                  {selectedAsset.invoice_url && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                        Invoice
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        <a href={selectedAsset.invoice_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
                          View Invoice →
                        </a>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Warranty & License Status */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2}>
                  Warranty and License
                </Typography>
                <Grid container spacing={2}>
                  {(selectedAsset.warranty_start || selectedAsset.warranty_end) && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                          Warranty Period
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          {statusChip(daysLeft(selectedAsset.warranty_end))}
                          {selectedAsset.warranty_end && (
                            <Typography variant="caption" color="text.secondary">
                              Expires: {toDMY(selectedAsset.warranty_end)}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </>
                  )}
                  {(selectedAsset.license_start || selectedAsset.license_end) && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                        License Period
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedAsset.license_start ? toDMY(selectedAsset.license_start) : '—'} → {selectedAsset.license_end ? toDMY(selectedAsset.license_end) : '—'}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* AMC / CMC Status */}
              {(selectedAsset.amc_status || selectedAsset.cmc_status) && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2}>
                    AMC / CMC Status
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedAsset.amc_status && selectedAsset.amc_status !== 'None' && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                            AMC Status
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedAsset.amc_status === 'ACTIVE' ? '✓ Active' : 
                             selectedAsset.amc_status === 'EXPIRED' ? '✕ Expired' :
                             selectedAsset.amc_status === 'PENDING' ? '⏳ Pending' : selectedAsset.amc_status}
                          </Typography>
                        </Grid>
                        {selectedAsset.amc_renewal_date && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                              AMC Renewal
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {toDMY(selectedAsset.amc_renewal_date)}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}
                    {selectedAsset.cmc_status && selectedAsset.cmc_status !== 'None' && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                            CMC Status
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedAsset.cmc_status === 'ACTIVE' ? '✓ Active' : 
                             selectedAsset.cmc_status === 'EXPIRED' ? '✕ Expired' :
                             selectedAsset.cmc_status === 'PENDING' ? '⏳ Pending' : selectedAsset.cmc_status}
                          </Typography>
                        </Grid>
                        {selectedAsset.cmc_renewal_date && (
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                              CMC Renewal
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {toDMY(selectedAsset.cmc_renewal_date)}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Paper>
              )}

              {/* Additional Info */}
              {(selectedAsset.details?.serial_number || selectedAsset.Device?.serial || selectedAsset.last_verified_at) && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={1}>Discovery details</Typography>
                  <Typography variant="body2">Serial number: {selectedAsset.details?.serial_number || selectedAsset.Device?.serial || "Not recorded"}</Typography>
                  {selectedAsset.last_verified_at && <Typography variant="body2">Last agent verification: {fmtDateTime(selectedAsset.last_verified_at)}</Typography>}
                </Paper>
              )}
              {selectedAsset.remarks && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={2}>
                    Additional Notes
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    {selectedAsset.remarks}
                  </Typography>
                </Paper>
              )}
            </Stack>
          ) : (
            <Typography color="text.secondary">No asset selected</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #e2e8f0', gap: 1 }}>
          <Button onClick={() => setAssetViewOpen(false)} sx={{ color: '#64748b' }}>
            Close
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button 
            variant="contained" 
            onClick={() => { 
              setAssetEditTab(0);
              setAssetEditOpen(true); 
              setAssetViewOpen(false);
              const a = selectedAsset || emptyAsset;
              setAssetForm({
                ...a,
                purchase_date: fmtDate(a.purchase_date),
                warranty_start: fmtDate(a.warranty_start),
                warranty_end: fmtDate(a.warranty_end),
                license_start: fmtDate(a.license_start),
                license_end: fmtDate(a.license_end),
                amc_renewal_date: fmtDate(a.amc_renewal_date),
                cmc_renewal_date: fmtDate(a.cmc_renewal_date),
                allocation_date: fmtDate(a.allocation_date),
                purchase_currency: a.purchase_currency || a.details?.purchase_currency || "INR",
              });
            }}
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            Edit Asset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custody Transfer Dialog */}
      <Dialog
        open={custodyTransferOpen}
        onClose={() => {
          setCustodyTransferOpen(false);
          setCustodyTransferForm({
            asset_custodian: "",
            asset_custodian_email: "",
            asset_custodian_contact: "",
          });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          }
        }}
      >
        <DialogTitle sx={{
          pb: 2,
          borderBottom: "1px solid #e2e8f0",
        }}>
          <Typography variant="h6" fontWeight={600}>
            Transfer Custody
          </Typography>
          {selectedAsset && (
            <Typography variant="caption" color="text.secondary">
              {selectedAsset.vendor_name || selectedAsset.asset_type} â€¢ {selectedAsset.asset_uid || selectedAsset.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.2} sx={{ mt: 0.5 }}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fbff", borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase" fontSize="0.7rem">
                Current Custodian
              </Typography>
              <Stack spacing={0.4} sx={{ mt: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {selectedAsset?.asset_custodian || "Not set"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAsset?.asset_custodian_email || "No email"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAsset?.asset_custodian_contact || "No contact number"}
                </Typography>
              </Stack>
            </Paper>
            <TextField
              fullWidth
              label="New Custodian Name"
              placeholder="Employee name"
              value={custodyTransferForm.asset_custodian}
              onChange={(e) => setCustodyTransferForm((f) => ({ ...f, asset_custodian: e.target.value }))}
              helperText="This should be the employee currently responsible for the asset."
            />
            <TextField
              fullWidth
              label="New Custodian Email"
              type="email"
              value={custodyTransferForm.asset_custodian_email}
              onChange={(e) => setCustodyTransferForm((f) => ({ ...f, asset_custodian_email: e.target.value }))}
              helperText="Optional email address"
            />
            <TextField
              fullWidth
              label="New Custodian Contact Number"
              value={custodyTransferForm.asset_custodian_contact}
              onChange={(e) => setCustodyTransferForm((f) => ({ ...f, asset_custodian_contact: e.target.value }))}
              helperText="Optional contact number"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => {
              setCustodyTransferOpen(false);
              setCustodyTransferForm({
                asset_custodian: "",
                asset_custodian_email: "",
                asset_custodian_contact: "",
              });
            }}
            sx={{ color: "#64748b" }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={custodyTransferSaving}
            onClick={submitCustodyTransfer}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
          >
            Transfer
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog 
        open={assetAssignOpen} 
        onClose={() => {
          setAssetAssignOpen(false);
          setDeviceAssignQ("");
        }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: '1px solid #e2e8f0'
        }}>
          <Typography variant="h6" fontWeight={600}>
            {selectedAsset?.device_id ? 'Transfer Asset to Device' : 'Assign Asset to Device'}
          </Typography>
          {selectedAsset && (
            <Typography variant="caption" color="text.secondary">
              {selectedAsset.vendor_name || selectedAsset.asset_type} • {selectedAsset.asset_uid || selectedAsset.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField 
            size="small" 
            label="Search devices" 
            placeholder="Search by hostname or UUID"
            fullWidth 
            value={deviceAssignQ}
            onChange={(e) => setDeviceAssignQ(e.target.value)} 
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          
          <Typography variant="subtitle2" fontWeight={600} color="#475569" mb={1.5}>
            Available Devices
          </Typography>
          
          {selectedAsset?.device_id && (
            <Button 
              fullWidth
              variant="outlined" 
              onClick={() => submitAssign("")}
              sx={{ 
                mb: 2, 
                py: 1.5,
                justifyContent: 'flex-start',
                borderColor: '#fca5a5',
                color: '#dc2626',
                '&:hover': {
                  borderColor: '#ef4444',
                  bgcolor: '#fef2f2'
                }
              }}
            >
              Unassign from current device
            </Button>
          )}
          
          <Stack spacing={1} sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {filteredDevicesForAssign.length > 0 ? (
              filteredDevicesForAssign.slice(0, 30).map(d => (
                <Button 
                  key={d.id} 
                  onClick={() => submitAssign(d.id)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    py: 1.5,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    borderColor: '#e2e8f0',
                    color: '#475569',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      bgcolor: '#f0f9ff',
                      color: '#1e293b'
                    }
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {d.hostname || 'Unnamed Device'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.device_uuid || `ID: ${d.id}`}
                    </Typography>
                  </Box>
                </Button>
              ))
            ) : (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#f8fafc' }}>
                <Typography color="text.secondary">
                  {deviceAssignQ.trim()
                    ? "No devices match your search"
                    : "No devices available for assignment"}
                </Typography>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => {
              setAssetAssignOpen(false);
              setDeviceAssignQ("");
            }}
            sx={{ color: '#64748b' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog
        open={bulkOpen}
        onClose={() => { setBulkOpen(false); setBulkResult(null); setBulkError(""); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        disableScrollLock
      >
        <DialogTitle sx={{
          pb: 2,
          borderBottom: '1px solid #e2e8f0',
        }}>
          <Typography variant="h6" fontWeight={600}>Bulk Upload Assets</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 6}}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload multiple assets at once using the Excel template.
          </Typography>
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Instructions</Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li>First, download the Excel template.</li>
                <li>Fill one row per asset. Fields marked with * are mandatory, including Owner Email.</li>
                <li>Dates should be in <strong>YYYY-MM-DD</strong> format.</li>
                <li>Do not change the column headers.</li>
              </ul>
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={async () => {
                try {
                  const res = await AssetsAPI.downloadTemplate();
                  const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'assets_bulk_template.xlsx';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) {
                  console.error(e);
                  alert(e?.response?.data?.error || e.message || 'Failed to download template');
                }
              }}
            >
              Download Template
            </Button>

            <Button
              variant="contained"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={bulkUploading}
            >
              {bulkUploading ? 'Uploading...' : 'Upload Assets'}
            </Button>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                setBulkUploading(true);
                setBulkError("");
                setBulkResult(null);
                try {
                  const res = await AssetsAPI.bulkUpload(file);
                  setBulkResult(res.data?.summary || null);
                  setRefreshKey(k => k + 1);
                } catch (err) {
                  console.error(err);
                  const apiError = err?.response?.data;
                  if (apiError?.missingHeaders) {
                    setBulkError(`Template is missing required columns: ${apiError.missingHeaders.join(', ')}`);
                  } else {
                    setBulkError(apiError?.error || err.message || 'Failed to upload assets');
                  }
                } finally {
                  setBulkUploading(false);
                }
              }}
            />
          </Stack>

          {bulkError && (
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
              <Typography variant="subtitle2" color="#b91c1c" sx={{ mb: 0.5 }}>Upload Error</Typography>
              <Typography variant="body2" color="#b91c1c">{bulkError}</Typography>
            </Box>
          )}

          {bulkResult && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#ecfdf3', border: '1px solid #bbf7d0' }}>
              <Typography variant="subtitle2" color="#166534" sx={{ mb: 1 }}>Upload Summary</Typography>
              <Typography variant="body2" color="#166534">
                Total rows processed: <strong>{bulkResult.totalRows}</strong><br />
                Assets added: <strong>{bulkResult.successCount}</strong><br />
                Failed rows: <strong>{bulkResult.failureCount}</strong>
              </Typography>
              {bulkResult.errors && bulkResult.errors.length > 0 && (
                <Box sx={{ mt: 1.5, maxHeight: 180, overflowY: 'auto' }}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="subtitle2" color="#b91c1c" sx={{ mb: 0.5 }}>Row Errors</Typography>
                  {bulkResult.errors.map((err, idx) => (
                    <Typography key={idx} variant="body2" color="#b91c1c">
                      Row {err.row}: {err.message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => { setBulkOpen(false); setBulkResult(null); setBulkError(""); }} sx={{ color: '#64748b' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <AssetCustomFieldsDialog
        open={assetCustomFieldsOpen}
        onClose={() => setAssetCustomFieldsOpen(false)}
        onChanged={() => setRefreshKey((key) => key + 1)}
      />
      <Dialog open={disposalOpen} onClose={() => !disposalSaving && setDisposalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{disposalMode === "DISPOSE" ? "Finalize asset disposal" : "Retire asset from active inventory"}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.6} sx={{ mt: 1 }}>
            <Alert severity={disposalMode === "DISPOSE" ? "warning" : "info"}>
              {disposalMode === "DISPOSE"
                ? "This is the final lifecycle action. The asset will be marked Disposed and a Susha internal disposal certificate will become available."
                : "Retired assets are removed from active inventory and device assignment. You can finalize disposal later after receiving recycler, buyer, or donation evidence."}
            </Alert>
            <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2 }}>
              <Typography fontWeight={700}>{selectedAsset?.asset_uid || selectedAsset?.id}</Typography>
              <Typography variant="body2" color="text.secondary">{selectedAsset?.vendor_name || selectedAsset?.asset_type || "Asset"}</Typography>
            </Paper>
            <TextField select fullWidth label="Reason" value={disposalForm.reason} onChange={(event) => setDisposalForm((form) => ({ ...form, reason: event.target.value }))}>
              <MenuItem value="E_WASTE">E-waste / recycler</MenuItem>
              <MenuItem value="SOLD">Sold</MenuItem>
              <MenuItem value="DONATED">Donated</MenuItem>
              <MenuItem value="TRADE_IN">Trade-in</MenuItem>
              <MenuItem value="BEYOND_REPAIR">Beyond repair</MenuItem>
              <MenuItem value="RETURNED_TO_VENDOR">Returned to vendor</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </TextField>
            <TextField type="date" fullWidth label={disposalMode === "DISPOSE" ? "Disposal date" : "Retirement date"} value={disposalForm.disposal_date} onChange={(event) => setDisposalForm((form) => ({ ...form, disposal_date: event.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField fullWidth label="Recipient, buyer, recycler, or vendor" value={disposalForm.recipient_name} onChange={(event) => setDisposalForm((form) => ({ ...form, recipient_name: event.target.value }))} />
            {disposalMode === "DISPOSE" ? <TextField type="number" fullWidth label="Sale or recovery value (optional)" value={disposalForm.sale_amount} onChange={(event) => setDisposalForm((form) => ({ ...form, sale_amount: event.target.value }))} inputProps={{ min: 0, step: "0.01" }} /> : null}
            <TextField required multiline minRows={3} fullWidth label="Reason and condition notes" value={disposalForm.notes} onChange={(event) => setDisposalForm((form) => ({ ...form, notes: event.target.value }))} helperText="Describe the condition and why the asset is leaving service. At least 5 characters." />
            <Button component="label" variant="outlined" startIcon={<UploadFile />}>
              {disposalFiles.length ? `${disposalFiles.length} evidence file(s) selected` : "Attach recycler receipt, buyer invoice, or handover proof (optional)"}
              <input hidden type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(event) => setDisposalFiles(Array.from(event.target.files || []))} />
            </Button>
            <FormControl required component="fieldset" error={!disposalForm.confirmed}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Checkbox checked={disposalForm.confirmed} onChange={(event) => setDisposalForm((form) => ({ ...form, confirmed: event.target.checked }))} />
                <Typography variant="body2">I confirm this asset must be removed from active inventory.</Typography>
              </Stack>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposalOpen(false)} disabled={disposalSaving}>Cancel</Button>
          <LoadingButton color={disposalMode === "DISPOSE" ? "error" : "warning"} variant="contained" loading={disposalSaving} onClick={submitDisposal} disabled={!disposalForm.confirmed || disposalForm.notes.trim().length < 5}>
            {disposalMode === "DISPOSE" ? "Confirm disposal" : "Confirm retirement"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}




